import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown, { uriTransformer } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ToastContainer } from 'react-toastify';
import { subscribeToConductorChat } from '../../../channels/conductor_chat_channel';
import client from '../../../shared/api/client';
import ConductorAvatar from './ConductorAvatar';
import OpendateLogo from '../../shared/Icons/OpendateLogo';
import ToolCallIndicator from './ToolCallIndicator';
import { useToolCalls } from '../hooks/useToolCalls';
import { useUserManualSections } from '../hooks/useUserManualSections';
import { pendingApprovalsFromToolCalls } from '../services/confirmGate';
import ExistingAiPanel, { existingAiIntegrationPropType } from '../../../shared/ExistingAiPanel';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import VoiceComposerButton from './VoiceComposerButton';
import {
  Announcement01,
  ArrowRight,
  ArrowUp,
  BarChart10,
  BookOpen01,
  LifeBuoy01,
  Button,
  CalendarDate,
  Check,
  ChevronDown,
  ChevronUp,
  ClockRewind,
  ClockStopwatch,
  Conductor,
  Copy01,
  Database01,
  Dataflow01,
  Edit05,
  Eye,
  EyeOff,
  File06,
  LayersThree01,
  Lightbulb02,
  Maximize01,
  Menu,
  Menu01,
  MessagePlusSquare,
  Microphone01,
  MicrophoneOff01,
  Minimize01,
  Minus,
  Paperclip,
  Phone01,
  PhoneX,
  Plus,
  ReceiptCheck,
  Send01,
  Spinner,
  Stars02,
  Trash01,
  User01,
  XClose,
  Zap, copyText } from '@opendate-app/setlist';
import {
  buildEmptyStatePrompts,
  buildPromptCatalog,
  EMPTY_STATE_PROMPT_ICONS,
  promptGroupForTool
} from '../promptCatalog';
import ConductorLoadingState from './beautifui/ConductorLoadingState';
import ConductorResponseFooter from './beautifui/ConductorResponseFooter';
import ConductorPromptMenu from './beautifui/ConductorPromptMenu';
import PromptsToTryPanel from './PromptsToTryPanel';
import ConductorManualPanel from './ConductorManualPanel';
import ConductorCuesPanel from './ConductorCuesPanel';
import { setVoiceSessionMicrophoneEnabled, startVoiceSession, stopVoiceSession } from '../services/livekitVoiceSession';
import ConductorResourceCards from './ConductorResourceCards';
import ContextHeadroomBanner from './ContextHeadroomBanner';
import ThreadContinuationNotice from './ThreadContinuationNotice';
import ConductorThreadActivity from './ConductorThreadActivity';
import { advanceThreadUsage, classifiedResourceCards, createdCardsByMessage, messagesWithToolCalls, resourceCardsFromToolCalls, threadToolCallsFrom } from '../threadActivity';
import { splitStreamingContent, withholdPartialTableRow } from '../streamingMarkdown';
import { conductorTheme } from '../themeTokens';

const MARKDOWN_STYLES = `
  .venue-conductor-markdown p { margin: 0 0 8px 0; }
  .venue-conductor-markdown p:last-child { margin-bottom: 0; }
  .venue-conductor-markdown ul, .venue-conductor-markdown ol { margin: 4px 0; padding-left: 20px; }
  .venue-conductor-markdown li { margin: 2px 0; }
  .venue-conductor-markdown strong { font-weight: 600; }
  .venue-conductor-markdown code { background: ${conductorTheme.surfaceMuted}; color: ${conductorTheme.text}; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
  .venue-conductor-markdown pre { background: ${conductorTheme.surfaceMuted}; color: ${conductorTheme.text}; padding: 12px; border-radius: 6px; overflow-x: auto; margin: 8px 0; }
  .venue-conductor-markdown pre code { background: none; padding: 0; }
  .venue-conductor-markdown h1,
  .venue-conductor-markdown h2,
  .venue-conductor-markdown h3,
  .venue-conductor-markdown h4,
  .venue-conductor-markdown h5,
  .venue-conductor-markdown h6 { margin: 12px 0 8px 0; line-height: 1.3; }
  .venue-conductor-markdown h1 { font-size: 1.25rem; font-weight: 700; }
  .venue-conductor-markdown h2 { font-size: 1.125rem; font-weight: 600; }
  .venue-conductor-markdown h3 { font-size: 1rem; font-weight: 600; }
  .venue-conductor-markdown h4 { font-size: 0.95rem; font-weight: 600; }
  .venue-conductor-markdown h5 { font-size: 0.9rem; font-weight: 600; }
  .venue-conductor-markdown h6 { font-size: 0.875rem; font-weight: 600; }
  .venue-conductor-table-shell { border: 1px solid ${conductorTheme.borderBlue}; border-radius: 12px; margin: 10px 0 12px; overflow: hidden; background: ${conductorTheme.surface}; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06); }
  .venue-conductor-table-toolbar { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: linear-gradient(135deg, ${conductorTheme.surfaceSoft} 0%, ${conductorTheme.surfaceSubtle} 100%); border-bottom: 1px solid ${conductorTheme.border}; font-size: 11px; font-weight: 700; color: ${conductorTheme.textSoft}; text-transform: uppercase; letter-spacing: 0.04em; }
  .venue-conductor-table-toolbar span { margin-right: auto; }
  .venue-conductor-table-toolbar button { display: inline-flex; align-items: center; gap: 4px; border: 1px solid ${conductorTheme.borderMuted}; background: ${conductorTheme.surface}; color: ${conductorTheme.textSlate}; border-radius: 999px; padding: 4px 9px; font-size: 11px; font-weight: 700; cursor: pointer; text-transform: none; letter-spacing: 0; }
  @media (hover: hover) and (pointer: fine) {
    .venue-conductor-table-toolbar button:hover { background: ${conductorTheme.surfaceSoft}; border-color: ${conductorTheme.iconMuted}; }
  }
  .venue-conductor-table-scroll { overflow-x: auto; }
  .venue-conductor-markdown table { border-collapse: collapse; width: 100%; margin: 0; }
  .venue-conductor-markdown th, .venue-conductor-markdown td { border: 1px solid ${conductorTheme.border}; padding: 7px 10px; text-align: left; font-size: 13px; }
  .venue-conductor-markdown th { background: ${conductorTheme.surfaceSubtle}; font-weight: 700; color: ${conductorTheme.text}; }
  @keyframes venue-conductor-card-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .venue-conductor-card-enter { animation: venue-conductor-card-in 200ms cubic-bezier(0.23, 1, 0.32, 1) both; }
  @media (prefers-reduced-motion: reduce) {
    .venue-conductor-card-enter { animation: none; }
  }
  .venue-conductor-btn { transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1); }
  .venue-conductor-btn:active { transform: scale(0.97); }
  .venue-conductor-menu-item { background-color: transparent; }
  .venue-conductor-resource-card { transition: border-color 160ms ease, background-color 160ms ease; }
  @media (hover: hover) and (pointer: fine) {
    .venue-conductor-menu-item:hover { background-color: ${conductorTheme.surfaceSoft}; }
    .venue-conductor-resource-card:hover { border-color: var(--od-accent-border, #9cc6df) !important; background-color: ${conductorTheme.surfaceSoft} !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .venue-conductor-btn { transition: none; }
    .venue-conductor-btn:active { transform: none; }
  }
`;

const VOICE_STAGE_STYLES = `
  @keyframes venue-conductor-bounce {
    0%, 80%, 100% { transform: scale(0.85); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }
  @keyframes venue-conductor-wave {
    0%, 100% { transform: scaleY(0.45); }
    50% { transform: scaleY(1.25); }
  }
  @keyframes venue-conductor-voice-morph {
    0%, 100% { border-radius: 46% 54% 52% 48% / 48% 46% 54% 52%; transform: scale(1); }
    34% { border-radius: 58% 42% 48% 52% / 44% 58% 42% 56%; transform: scale(1.04); }
    68% { border-radius: 42% 58% 56% 44% / 56% 44% 58% 42%; transform: scale(0.98); }
  }
  @keyframes venue-conductor-voice-ring {
    0% { opacity: 0.42; transform: scale(0.82); }
    70%, 100% { opacity: 0; transform: scale(1.28); }
  }
  .venue-conductor-voice-orb {
    position: relative;
    width: 156px;
    height: 156px;
    border-radius: 50%;
    border: 1px solid ${conductorTheme.voiceOrbBorder};
    background: ${conductorTheme.voiceOrbBackground};
    box-shadow: ${conductorTheme.voiceOrbShadow};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .venue-conductor-voice-orb::before,
  .venue-conductor-voice-orb::after {
    content: '';
    position: absolute;
    inset: -18px;
    border-radius: inherit;
    border: 1px solid ${conductorTheme.voiceOrbPrimaryRing};
    opacity: 0;
    pointer-events: none;
  }
  .venue-conductor-voice-orb::after {
    inset: -31px;
    border-color: ${conductorTheme.voiceOrbSecondaryRing};
  }
  .venue-conductor-voice-orb--active {
    border-color: ${conductorTheme.voiceOrbActiveBorder};
    background: ${conductorTheme.voiceOrbActiveBackground};
    animation: venue-conductor-voice-morph 3.2s ease-in-out infinite;
  }
  .venue-conductor-voice-orb--active::before {
    animation: venue-conductor-voice-ring 1.8s ease-out infinite;
  }
  .venue-conductor-voice-orb--active::after {
    animation: venue-conductor-voice-ring 1.8s ease-out 0.42s infinite;
  }
  .venue-conductor-voice-orb--paused {
    background: ${conductorTheme.voiceOrbPausedBackground};
    border-color: ${conductorTheme.borderBlueStrong};
  }
  @media (prefers-reduced-motion: reduce) {
    .venue-conductor-voice-orb--active,
    .venue-conductor-voice-orb--active::before,
    .venue-conductor-voice-orb--active::after { animation: none !important; }
  }
`;

const getIsMobileLayout = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < 768;
};

const VENUE_CONDUCTOR_THREADS_PATH = '/conductor/venue_chat/threads';

const VISIBLE_INSIGHT_LIMIT = 2;

const venueConductorThreadPath = (threadId) => `${VENUE_CONDUCTOR_THREADS_PATH}/${threadId}`;

const venueConductorThreadMessagesPath = (threadId) => `${venueConductorThreadPath(threadId)}/messages`;

const venueConductorThreadCompactionPath = (threadId) => `${venueConductorThreadPath(threadId)}/compaction`;

const venueConductorThreadToolApprovalsPath = (threadId) => `${venueConductorThreadPath(threadId)}/tool_approvals`;

const voiceNudgeStorageKey = (teamId) => (
  teamId ? `venue_conductor_voice_nudge_seen_${teamId}` : 'venue_conductor_voice_nudge_seen'
);

const micTipStorageKey = (teamId) => (
  teamId ? `venue_conductor_mic_tip_seen_${teamId}` : 'venue_conductor_mic_tip_seen'
);

const CONDUCTOR_DISCOVERY_STYLES = `
  @keyframes conductor-mic-pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 89, 94, 0.45); }
    70% { box-shadow: 0 0 0 18px rgba(255, 89, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 89, 94, 0); }
  }
  @keyframes conductor-tip-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes conductor-compact-history-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .venue-conductor-compact-thread-trigger:focus-visible,
  .venue-conductor-compact-thread-option:focus-visible,
  .venue-conductor-compact-thread-close:focus-visible {
    outline: 2px solid var(--setlist-border-brand, #2a9ce4);
    outline-offset: 2px;
  }
  @media (hover: hover) and (pointer: fine) {
    .venue-conductor-compact-thread-trigger:hover,
    .venue-conductor-compact-thread-option:hover,
    .venue-conductor-compact-thread-close:hover {
      background-color: ${conductorTheme.surfaceMuted} !important;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .conductor-mic-cta { animation: none !important; }
    .venue-conductor-compact-thread-history { animation: none !important; }
  }
`;

const voiceErrorMessage = (error) => {
  if (error?.message) {
    return error.message;
  }

  return 'Unable to start voice mode.';
};

const formatVoiceElapsed = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const composerActionButtonStyle = {
  width: '44px',
  height: '44px',
  padding: 0,
  borderRadius: '8px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 1
};

const composerMenuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  border: 'none',
  color: conductorTheme.textStrong,
  textAlign: 'left',
  borderRadius: '10px',
  padding: '9px 10px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '1.35',
  cursor: 'pointer'
};

const composerMenuIconStyle = {
  width: '18px',
  marginRight: '6px',
  color: conductorTheme.textSlate
};

const composerMenuCheckStyle = {
  marginLeft: 'auto',
  color: conductorTheme.accentText
};

const MESSAGE_DIRECTIVE_START = '[Conductor instructions]';
const MESSAGE_DIRECTIVE_END = '[/Conductor instructions]';
const EXPORT_READY_DIRECTIVE = {
  id: 'export_ready',
  kind: 'Export-ready',
  label: 'One-table CSV-ready response',
  prompt: 'Return the answer as one markdown table with clear column headers when a single table can represent the data, so I can copy it or download it as one CSV.'
};

const DATE_RANGE_SCOPES = [
  {
    value: 'sold',
    label: 'Sales sold date',
    prompt: (startDate, endDate) => `Use sold_start_date=${startDate} and sold_end_date=${endDate} for this request.`
  },
  {
    value: 'event',
    label: 'Event date',
    prompt: (startDate, endDate) => `Use event_start_date=${startDate} and event_end_date=${endDate} for this request.`
  },
  {
    value: 'financial',
    label: 'Financial date',
    prompt: (startDate, endDate) => `Use start_date=${startDate} and end_date=${endDate} for venue_financials in this request.`
  }
];

const NEW_CUE_PROMPT = 'Help me set up a new cue.';

const ACTION_PROMPTS = [
  'Based on this thread, recommend the next action I should take and what data supports it.',
  'Find events that need attention and return the reasons in a table.',
  'Turn the latest result into a concise action plan with owners and next steps.'
];

const SOURCE_ICONS = {
  calendar: CalendarDate,
  sales: BarChart10,
  marketing: Announcement01,
  booking: Edit05,
  finances: ReceiptCheck,
  howto: BookOpen01
};

const toolSummaryHeadline = (summary) => (summary ? summary.trim().split(/(?<=\.)\s/)[0] : null);

const sourcesForToolCalls = (toolCalls = []) => toolCalls
  .filter((toolCall) => toolCall.success !== false && !toolCall.awaiting_approval)
  .map((toolCall, index) => ({
    key: `${toolCall.id || toolCall.tool_name}-${index}`,
    name: toolCall.display_name || toolCall.tool_name,
    meta: toolSummaryHeadline(toolCall.summary),
    icon: SOURCE_ICONS[promptGroupForTool(toolCall.tool_name)] || Database01
  }));

const todayInputDate = () => new Date().toISOString().slice(0, 10);

const monthStartInputDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
};

const buildMessageContent = (content, directives) => {
  const trimmedContent = content.trim();
  if (directives.length === 0) return trimmedContent;

  const directiveLines = directives.map((directive) => (
    `- ${directive.kind} | ${directive.label} | ${directive.prompt}`
  ));

  return `${MESSAGE_DIRECTIVE_START}\n${directiveLines.join('\n')}\n${MESSAGE_DIRECTIVE_END}\n\n${trimmedContent}`;
};

const parseMessageContent = (content) => {
  const rawContent = content?.toString() || '';
  const trimmedStartContent = rawContent.trimStart();

  if (!trimmedStartContent.startsWith(MESSAGE_DIRECTIVE_START)) {
    return { directives: [], content: rawContent };
  }

  const directiveEndIndex = trimmedStartContent.indexOf(MESSAGE_DIRECTIVE_END);
  if (directiveEndIndex === -1) {
    return { directives: [], content: rawContent };
  }

  const directiveBlock = trimmedStartContent
    .slice(MESSAGE_DIRECTIVE_START.length, directiveEndIndex)
    .trim();
  const visibleContent = trimmedStartContent
    .slice(directiveEndIndex + MESSAGE_DIRECTIVE_END.length)
    .trim();

  const directives = directiveBlock
    .split('\n')
    .map((line) => line.trim().replace(/^-\s*/, ''))
    .filter(Boolean)
    .map((line) => {
      const [kind, label, ...promptParts] = line.split(' | ');
      return {
        kind: kind || 'Instruction',
        label: label || line,
        prompt: promptParts.join(' | ')
      };
    });

  return {
    directives,
    content: visibleContent || rawContent
  };
};

const directiveIcon = (directive) => {
  if (directive.id === 'date_range' || directive.kind === 'Date range') return CalendarDate;
  if (directive.id === 'export_ready' || directive.kind === 'Export-ready') return File06;

  return Stars02;
};

const DirectiveIcon = ({ directive, size }) => {
  const Icon = directiveIcon(directive);
  return <Icon width={size} height={size} aria-hidden="true" />;
};

DirectiveIcon.propTypes = {
  directive: PropTypes.shape({ id: PropTypes.string, kind: PropTypes.string }).isRequired,
  size: PropTypes.number.isRequired
};

const ComposerSpinner = () => <Spinner size={18} color="currentColor" />;

const tableRowsFromElement = (table) => {
  if (!table) return [];

  return Array.from(table.querySelectorAll('tr')).map((row) => (
    Array.from(row.querySelectorAll('th, td')).map((cell) => cell.innerText.trim().replace(/\s+/g, ' '))
  )).filter((row) => row.length > 0);
};

const csvEscape = (value) => {
  const stringValue = value == null ? '' : value.toString();
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const rowsToCsv = (rows) => rows.map((row) => row.map(csvEscape).join(',')).join('\n');

const tsvEscape = (value) => {
  const stringValue = value == null ? '' : value.toString();
  return stringValue.replace(/[\t\n\r]/g, ' ');
};

const rowsToTsv = (rows) => rows.map((row) => row.map(tsvEscape).join('\t')).join('\n');

const MarkdownTable = ({ children }) => {
  const tableRef = useRef(null);

  const handleCopyTable = async () => {
    const rows = tableRowsFromElement(tableRef.current);
    if (rows.length === 0) return;

    try {
      await copyText(rowsToTsv(rows));
      toastSuccess('Table copied for spreadsheets');
    } catch (e) {
      toastError('Failed to copy table');
    }
  };

  const handleDownloadCsv = () => {
    const rows = tableRowsFromElement(tableRef.current);
    if (rows.length === 0) return;

    const blob = new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conductor-table-${todayInputDate()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toastSuccess('CSV downloaded');
  };

  return (
    <div className="venue-conductor-table-shell">
      <div className="venue-conductor-table-toolbar">
        <span>Table</span>
        <button type="button" onClick={handleCopyTable}>
          <Copy01 width={12} height={12} aria-hidden="true" /> Copy
        </button>
        <button type="button" onClick={handleDownloadCsv}>
          <File06 width={12} height={12} aria-hidden="true" /> CSV
        </button>
      </div>
      <div className="venue-conductor-table-scroll">
        <table ref={tableRef}>{children}</table>
      </div>
    </div>
  );
};

MarkdownTable.propTypes = {
  children: PropTypes.node.isRequired
};

const ManualLinkContext = React.createContext(null);

const MANUAL_LINK_PATTERN = /^(?:https?:\/\/[^/]+)?\/user_manual#(.+)$/;

const manualArticleKeyFromHref = (href) => {
  const match = MANUAL_LINK_PATTERN.exec(href || '');
  return match ? match[1] : null;
};

const MarkdownLink = ({ href, title, children }) => {
  const openManualArticle = React.useContext(ManualLinkContext);
  const manualKey = manualArticleKeyFromHref(href);

  if (openManualArticle && manualKey) {
    return (
      <a
        href={href}
        title={title}
        onClick={(event) => {
          event.preventDefault();
          openManualArticle(manualKey);
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

MarkdownLink.propTypes = {
  href: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node
};

MarkdownLink.defaultProps = {
  href: undefined,
  title: undefined,
  children: null
};

const markdownComponents = {
  table: MarkdownTable,
  a: MarkdownLink
};

const transformMarkdownLinkUri = (uri) => (uri.startsWith('codex://') ? uri : uriTransformer(uri));

// Completed blocks of a streaming message never change once a stable boundary
// passes them, so memoizing skips re-parsing the whole transcript per flush.
const MarkdownBlock = React.memo(function MarkdownBlock({ block }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} transformLinkUri={transformMarkdownLinkUri}>
      {block}
    </ReactMarkdown>
  );
});

MarkdownBlock.propTypes = {
  block: PropTypes.string.isRequired
};

const VenueConductorPage = ({
  csrfToken,
  containerHeight = 'calc(100vh - 70px)',
  teamId = null,
  teamName = null,
  currentUserAvatarUrl = null,
  existingAiIntegration = null,
  conductorVoiceEnabled = false,
  helpCenterEnabled = false,
  conductorTextingEnabled = false,
  conductorCuesEnabled = false,
  renderToastContainer = true,
  onCloseConductor = null,
  onExpandConductor = null,
  onMinimizeConductor = null,
  onVoiceStateChange = null,
  voiceEndRequestId = 0,
  orbHidden = false,
  toggleOrb = null,
  initialPrompt = null,
  autoStartVoice = false,
  promptContext = null,
  initialThreadId = null,
  onActiveThreadChange = null,
  conductorInterface = 'page',
  entryPoint = 'page',
  layoutMode = 'full'
}) => {
  const compactLayout = layoutMode === 'compact';
  const condensedLayout = layoutMode === 'condensed' || compactLayout;
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadUsage, setThreadUsage] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [showPromptsPanel, setShowPromptsPanel] = useState(false);
  const [isManualPanelOpen, setIsManualPanelOpen] = useState(false);
  const [cuesPanelView, setCuesPanelView] = useState(null);
  const [pendingNewThreadPrompt, setPendingNewThreadPrompt] = useState(null);
  const [hasPendingInsight, setHasPendingInsight] = useState(false);
  const [showAllEmptyStatePrompts, setShowAllEmptyStatePrompts] = useState(false);
  const userManual = useUserManualSections(helpCenterEnabled && isManualPanelOpen);
  const [manualArticleRequest, setManualArticleRequest] = useState(null);
  const manualArticleRequestCount = useRef(0);
  const openManualArticle = useCallback((key) => {
    manualArticleRequestCount.current += 1;
    setManualArticleRequest({ key, id: manualArticleRequestCount.current });
    setCuesPanelView(null);
    setIsManualPanelOpen(true);
  }, []);
  const manualLinkHandler = helpCenterEnabled ? openManualArticle : null;
  const [fetchedPromptContext, setFetchedPromptContext] = useState(null);
  const effectivePromptContext = promptContext || fetchedPromptContext;
  const [isSending, setIsSending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [toolCallMessageId, setToolCallMessageId] = useState(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(getIsMobileLayout);
  const [isThreadSidebarOpen, setIsThreadSidebarOpen] = useState(() => !getIsMobileLayout() && !condensedLayout);
  // Docked (condensed) mode is too narrow for the fixed Conversations column, so
  // it borrows the mobile overlay treatment while keeping desktop styling.
  const isOverlayThreads = isMobileLayout || condensedLayout;
  const mcpPanelDismissedKey = `venue_conductor_ai_panel_dismissed_${teamId || 'default'}`;
  const [isMcpPanelDismissed, setIsMcpPanelDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(mcpPanelDismissedKey) === 'true';
    } catch (e) {
      return false;
    }
  });
  const dismissMcpPanel = () => {
    setIsMcpPanelDismissed(true);
    try {
      window.localStorage.setItem(mcpPanelDismissedKey, 'true');
    } catch (e) {
      return;
    }
  };
  const [isDateRangePanelOpen, setIsDateRangePanelOpen] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState(monthStartInputDate);
  const [dateRangeEnd, setDateRangeEnd] = useState(todayInputDate);
  const [dateRangeScope, setDateRangeScope] = useState(DATE_RANGE_SCOPES[0].value);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [composerDirectives, setComposerDirectives] = useState([]);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceElapsedSeconds, setVoiceElapsedSeconds] = useState(0);
  const [voiceError, setVoiceError] = useState(null);
  const [voiceActivity, setVoiceActivity] = useState(null);
  const [isVoiceOrbMinimized, setIsVoiceOrbMinimized] = useState(false);
  const [isVoiceNudgeVisible, setIsVoiceNudgeVisible] = useState(false);
  const [isMicTipVisible, setIsMicTipVisible] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [contextUsage, setContextUsage] = useState(null);
  const [summaryBoundary, setSummaryBoundary] = useState(null);
  const [threadHandoff, setThreadHandoff] = useState(null);
  const [isCompacting, setIsCompacting] = useState(false);
  const [isCreatingHandoff, setIsCreatingHandoff] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const isPinnedToBottomRef = useRef(true);
  const pendingSmoothScrollRef = useRef(false);
  const streamBufferRef = useRef('');
  const streamFlushFrameRef = useRef(null);
  const streamingMessageIdRef = useRef(null);
  const subscriptionRef = useRef(null);
  const subscriptionThreadIdRef = useRef(null);
  const subscriptionConnectedRef = useRef(false);
  const subscriptionWaitersRef = useRef([]);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const pendingUserMessageIdRef = useRef(null);
  const selectedThreadIdRef = useRef(null);
  const messageLoadRequestIdRef = useRef(0);
  const voiceNudgeScheduledRef = useRef(false);
  const micTipScheduledRef = useRef(false);
  const voiceSessionRef = useRef(null);
  const voiceStartedAtRef = useRef(null);
  const isMountedRef = useRef(true);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    setUploadProgress((progress) => {
      if (!progress || progress.threadId !== activeThreadId || progress.jobId == null) return progress;
      const jobMessages = messages.filter((message) => message.metadata?.job_id === progress.jobId);
      if (jobMessages.some((message) => ['processing_complete', 'processing_failed'].includes(message.metadata.type))) return null;
      const classificationMessage = jobMessages.find((message) => message.metadata.type === 'processing_classified');
      if (!classificationMessage) return progress;
      const foundOffer = (classificationMessage.metadata.classifications || []).some((classification) => classification.document_type === 'offer');
      const label = foundOffer ? 'Building the offer' : 'Reading the details';
      return progress.label === label ? progress : { ...progress, label };
    });
  }, [messages, activeThreadId, uploadProgress?.jobId]);

  const {
    activeToolCalls,
    completedToolCalls,
    handleToolStarted,
    handleToolCompleted,
    resetToolCalls,
    setInitialToolCalls
  } = useToolCalls();

  // Ref avoids a stale closure: onComplete's effect doesn't depend on completedToolCalls.
  const completedToolCallsRef = useRef([]);
  useEffect(() => {
    completedToolCallsRef.current = completedToolCalls;
  }, [completedToolCalls]);

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
    'Accept': 'application/json'
  };

  const resetStreamBuffer = useCallback(() => {
    if (streamFlushFrameRef.current != null) {
      window.cancelAnimationFrame(streamFlushFrameRef.current);
      streamFlushFrameRef.current = null;
    }
    streamBufferRef.current = '';
  }, []);

  const flushStreamBuffer = useCallback(() => {
    const buffered = streamBufferRef.current;
    if (!buffered) return;

    streamBufferRef.current = '';
    setStreamingMessage((prev) => ({
      id: prev?.id ?? streamingMessageIdRef.current,
      content: (prev?.content || '') + buffered
    }));
  }, []);

  const resetThreadView = useCallback((threadId = null) => {
    selectedThreadIdRef.current = threadId;
    messageLoadRequestIdRef.current += 1;
    setMessages([]);
    setUploadProgress(null);
    setThreadUsage(null);
    setStreamingMessage(null);
    setToolCallMessageId(null);
    setIsSending(false);
    setIsLoadingMessages(false);
    resetToolCalls();
    resetStreamBuffer();
    isPinnedToBottomRef.current = true;
    setContextUsage(null);
    setSummaryBoundary(null);
    setThreadHandoff(null);
    pendingUserMessageIdRef.current = null;
  }, [resetToolCalls, resetStreamBuffer]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesScrollRef.current;
    if (!container) return;

    isPinnedToBottomRef.current =
      container.scrollHeight - container.scrollTop - container.clientHeight < 80;
  }, []);

  const reconcileUserMessage = useCallback((message) => {
    if (!message) return;

    setMessages((prev) => {
      const pendingMessageId = pendingUserMessageIdRef.current;
      const pendingIndex = pendingMessageId ? prev.findIndex((existingMessage) => existingMessage.id === pendingMessageId) : -1;
      const nextMessages = prev.filter((existingMessage) => existingMessage.id !== pendingMessageId && existingMessage.id !== message.id);

      if (pendingIndex >= 0) {
        nextMessages.splice(Math.min(pendingIndex, nextMessages.length), 0, message);
        return nextMessages;
      }

      return [...nextMessages, message];
    });

    pendingUserMessageIdRef.current = null;
  }, []);

  const appendAssistantMessage = useCallback((message) => {
    if (!message) return;

    setMessages((prev) => {
      if (prev.some((existingMessage) => existingMessage.id === message.id)) {
        return prev;
      }

      return [...prev, message];
    });
  }, []);

  const removePendingUserMessage = useCallback(() => {
    const pendingMessageId = pendingUserMessageIdRef.current;
    if (!pendingMessageId) return;

    setMessages((prev) => prev.filter((message) => message.id !== pendingMessageId));
    pendingUserMessageIdRef.current = null;
  }, []);

  const settleSubscriptionWaiters = useCallback((threadId, isConnected) => {
    const matchingWaiters = [];
    const remainingWaiters = [];

    subscriptionWaitersRef.current.forEach((waiter) => {
      if (waiter.threadId === threadId) {
        matchingWaiters.push(waiter);
      } else {
        remainingWaiters.push(waiter);
      }
    });

    subscriptionWaitersRef.current = remainingWaiters;
    matchingWaiters.forEach((waiter) => {
      window.clearTimeout(waiter.timeoutId);
      waiter.resolve(isConnected);
    });
  }, []);

  const waitForSubscriptionConnection = useCallback((threadId, timeoutMs = 1500) => {
    if (subscriptionThreadIdRef.current === threadId && subscriptionConnectedRef.current) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const waiter = {
        threadId,
        resolve,
        timeoutId: window.setTimeout(() => {
          subscriptionWaitersRef.current = subscriptionWaitersRef.current.filter((pendingWaiter) => pendingWaiter !== waiter);
          resolve(false);
        }, timeoutMs)
      };

      subscriptionWaitersRef.current = [...subscriptionWaitersRef.current, waiter];
    });
  }, []);

  const buildThreadTitleFromContent = useCallback((content) => {
    const normalizedContent = parseMessageContent(content).content.toString().trim().replace(/\s+/g, ' ');
    if (!normalizedContent) {
      return 'New Chat';
    }

    if (normalizedContent.length <= 50) {
      return normalizedContent;
    }

    return `${normalizedContent.slice(0, 50)}...`;
  }, []);

  const updateThreadSummary = useCallback((threadId, updater) => {
    setThreads((prev) => {
      const threadIndex = prev.findIndex((thread) => thread.id === threadId);
      if (threadIndex === -1) {
        return prev;
      }

      const updatedThread = updater(prev[threadIndex]);
      const nextThreads = prev.filter((thread) => thread.id !== threadId);

      return [updatedThread, ...nextThreads];
    });
  }, []);

  const recordUserMessageInThreadList = useCallback((threadId, message) => {
    if (!message) return;

    updateThreadSummary(threadId, (thread) => {
      const shouldReplaceTitle = !thread.title || thread.title === 'New Chat';

      return {
        ...thread,
        title: shouldReplaceTitle ? buildThreadTitleFromContent(message.content) : thread.title,
        message_count: (thread.message_count || 0) + 1,
        last_message_at: message.created_at || new Date().toISOString()
      };
    });
  }, [buildThreadTitleFromContent, updateThreadSummary]);

  const recordAssistantMessageInThreadList = useCallback((threadId, message) => {
    if (!message) return;

    updateThreadSummary(threadId, (thread) => ({
      ...thread,
      message_count: (thread.message_count || 0) + 1,
      last_message_at: message.created_at || new Date().toISOString()
    }));
  }, [updateThreadSummary]);

  const handleVoiceSessionFailure = useCallback((data) => {
    const voiceSession = voiceSessionRef.current;
    if (!voiceSession) return;

    const activeVoiceSessionId = voiceSession.credentials?.voice_session_id;
    if (data?.voice_session_id && activeVoiceSessionId && data.voice_session_id !== activeVoiceSessionId) {
      return;
    }

    voiceSessionRef.current = null;
    voiceStartedAtRef.current = null;
    setVoiceStatus('idle');
    setVoiceElapsedSeconds(0);
    setVoiceActivity(null);
    setIsVoiceOrbMinimized(false);
    setVoiceError(data?.message || 'Voice transcription stopped.');
    stopVoiceSession(voiceSession);
  }, []);

  const handleVoiceTranscript = useCallback((data) => {
    const activeVoiceSessionId = voiceSessionRef.current?.credentials?.voice_session_id;
    if (data?.voice_session_id && activeVoiceSessionId && data.voice_session_id !== activeVoiceSessionId) {
      return;
    }

    if (data?.type === 'voice_activity') {
      if (data.activity === 'speech_started') {
        setVoiceActivity('speaking');
      } else if (data.activity === 'speech_stopped' || data.activity === 'committed') {
        setVoiceActivity('processing');
      }
      return;
    }

    const transcript = data?.transcript?.toString().trim();
    if (!transcript) return;

    setVoiceActivity(data.is_final === true ? 'processing' : 'transcribing');
  }, []);

  // Smooth scroll is reserved for discrete moments (sending, turn completion).
  // Per-chunk streaming scrolls instantly, and only while the user is already
  // pinned to the bottom — restarting a smooth scroll dozens of times a second
  // is what made streaming feel choppy.
  useEffect(() => {
    if (isLoadingMessages) return;
    if (!isPinnedToBottomRef.current) return;

    scrollToBottom(pendingSmoothScrollRef.current ? 'smooth' : 'auto');
    pendingSmoothScrollRef.current = false;
  }, [messages, streamingMessage, activeToolCalls, isLoadingMessages, scrollToBottom]);

  useEffect(() => resetStreamBuffer, [resetStreamBuffer]);

  // When a new turn's tool calls start while the anchor still points at an
  // already-rendered message, that anchor is stale — voice turns broadcast tool
  // activity before their stream_start. Clear it so the tool-call indicator
  // renders in sequence at the bottom instead of above the previous message.
  useEffect(() => {
    if (activeToolCalls.length === 0 || toolCallMessageId == null) return;
    if (messages.some((message) => message.id === toolCallMessageId)) {
      setToolCallMessageId(null);
    }
  }, [activeToolCalls.length, toolCallMessageId, messages]);

  useEffect(() => {
    selectedThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  // Skip the mount-time null so a fresh page load can't wipe the persisted
  // thread before the restore effect below has had a chance to reselect it.
  const hasNotifiedThreadChangeRef = useRef(false);
  useEffect(() => {
    if (!onActiveThreadChange) {
      return;
    }
    if (!hasNotifiedThreadChangeRef.current && activeThreadId == null) {
      return;
    }

    hasNotifiedThreadChangeRef.current = true;
    onActiveThreadChange(activeThreadId);
  }, [activeThreadId, onActiveThreadChange]);

  useEffect(() => {
    if (!isMobileLayout) {
      setIsThreadSidebarOpen(!condensedLayout);
    }
  }, [condensedLayout, isMobileLayout]);

  useEffect(() => {
    loadThreads();
  }, []);

  // Personalized prompt names (venue/show) are fetched lazily so the app-wide
  // modal mount adds zero queries to ordinary page loads; a provided prop wins.
  useEffect(() => {
    if (promptContext || fetchedPromptContext || typeof window.fetch !== 'function') {
      return undefined;
    }

    let cancelled = false;
    window.fetch('/conductor/venue_chat/prompt_context', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) setFetchedPromptContext(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [promptContext, fetchedPromptContext]);

  const shouldShowEmptyState =
    !isLoadingMessages &&
    messages.length === 0 &&
    streamingMessage == null &&
    uploadProgress == null &&
    activeToolCalls.length === 0 &&
    completedToolCalls.length === 0 &&
    !threadHandoff;

  useEffect(() => {
    if (compactLayout) {
      return undefined;
    }

    let cancelled = false;
    let latestRequest = 0;
    const loadInsights = () => {
      const request = ++latestRequest;
      client.get('/conductor/venue_chat/insights')
        .then(({ data }) => {
          if (cancelled) return;
          if (request !== latestRequest) return;
          const fetched = data.insights || [];
          setInsights(fetched);
          setHasPendingInsight(fetched.some((insight) => insight.status === 'pending'));
          window.dispatchEvent(new CustomEvent('conductorInsightsChanged', {
            detail: { pendingCount: fetched.filter((insight) => insight.status === 'pending').length }
          }));
        })
        .catch(() => {});
    };
    loadInsights();
    window.addEventListener('conductorInsightsRefreshRequested', loadInsights);
    return () => {
      cancelled = true;
      window.removeEventListener('conductorInsightsRefreshRequested', loadInsights);
    };
  }, [compactLayout]);

  // Only an insight the user could actually see counts as seen -- mounting with
  // Conductor already open (a refresh) must not silently clear the launcher dot.
  const insightsMarkedSeenRef = useRef(false);
  useEffect(() => {
    if (compactLayout) return;
    if (insightsMarkedSeenRef.current) return;
    if (!shouldShowEmptyState || insights.length === 0) return;

    insightsMarkedSeenRef.current = true;
    client.get('/conductor/venue_chat/insights', { params: { mark_seen: true } })
      .then(() => {
        window.dispatchEvent(new CustomEvent('conductorInsightsChanged', { detail: { pendingCount: 0 } }));
      })
      .catch(() => {});
  }, [compactLayout, shouldShowEmptyState, insights]);

  const dismissInsight = useCallback(async (insightToDismiss) => {
    const insightId = insightToDismiss.id;
    setInsights((prev) => prev.filter((insight) => insight.id !== insightId));
    try {
      await client.patch(`/conductor/venue_chat/insights/${insightId}/dismiss`);
      window.dispatchEvent(new CustomEvent('conductorInsightsRefreshRequested'));
    } catch (e) {
      // The card is already gone locally; the insight expires server-side regardless.
      setInsights((prev) => prev.some((insight) => insight.id === insightId)
        ? prev
        : [insightToDismiss, ...prev]);
      toastError('Could not dismiss this insight. Please try again.');
    }
  }, []);

  // Restore the persisted thread once the list arrives (dock survives full-page
  // navigations; the wrapper remembers which conversation was open). One-shot:
  // never re-hijack after the user has interacted.
  const initialThreadAppliedRef = useRef(false);
  useEffect(() => {
    if (initialThreadAppliedRef.current || !initialThreadId || isLoadingThreads) {
      return;
    }
    if (isSending || pendingUserMessageIdRef.current) {
      return;
    }

    initialThreadAppliedRef.current = true;
    if (activeThreadId == null && threads.some((thread) => thread.id === initialThreadId)) {
      selectThread(initialThreadId);
    }
  }, [initialThreadId, isLoadingThreads, threads, activeThreadId]);


  useEffect(() => {
    const handleResize = () => {
      const nextIsMobileLayout = getIsMobileLayout();
      setIsMobileLayout(nextIsMobileLayout);
      setIsThreadSidebarOpen(!nextIsMobileLayout && !condensedLayout);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [condensedLayout]);

  useEffect(() => {
    if (!isMobileLayout) return undefined;
    if (typeof document === 'undefined') return undefined;

    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;

    const originalContent = meta.getAttribute('content');
    meta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, shrink-to-fit=no, viewport-fit=cover'
    );

    return () => {
      if (originalContent) {
        meta.setAttribute('content', originalContent);
      }
    };
  }, [isMobileLayout]);

  useEffect(() => {
    if (!isActionMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setIsActionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isActionMenuOpen]);

  useEffect(() => {
    if (voiceStatus !== 'listening' && voiceStatus !== 'paused') {
      return undefined;
    }

    const updateElapsedSeconds = () => {
      const startedAt = voiceStartedAtRef.current || Date.now();
      setVoiceElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };

    updateElapsedSeconds();
    const intervalId = window.setInterval(updateElapsedSeconds, 1000);

    return () => window.clearInterval(intervalId);
  }, [voiceStatus]);

  useEffect(() => {
    if (!isMobileLayout || voiceNudgeScheduledRef.current) {
      return undefined;
    }

    let hasSeenVoiceNudge = false;

    try {
      hasSeenVoiceNudge = localStorage.getItem(voiceNudgeStorageKey(teamId)) === 'seen';
    } catch (e) {
      hasSeenVoiceNudge = true;
    }

    if (hasSeenVoiceNudge) {
      return undefined;
    }

    voiceNudgeScheduledRef.current = true;
    const timer = window.setTimeout(() => {
      setIsVoiceNudgeVisible(true);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [isMobileLayout, teamId]);

  // First-time tooltip pointing at the composer mic (desktop only — mobile gets
  // the big "Try voice mode" button + voice nudge). The relaunch launch modal now
  // pops on app load from VenueConductorModal, so this just gates on its own
  // once-per-team key and writes "seen" only when it actually shows.
  useEffect(() => {
    if (isMobileLayout || !conductorVoiceEnabled || micTipScheduledRef.current) {
      return undefined;
    }

    let hasSeenMicTip = false;
    try {
      hasSeenMicTip = localStorage.getItem(micTipStorageKey(teamId)) === 'seen';
    } catch (e) {
      hasSeenMicTip = true;
    }

    micTipScheduledRef.current = true;

    if (hasSeenMicTip) {
      return undefined;
    }

    const showTimer = window.setTimeout(() => {
      setIsMicTipVisible(true);
      try {
        localStorage.setItem(micTipStorageKey(teamId), 'seen');
      } catch (e) {
        // Local storage is optional for the tip.
      }
    }, 1200);
    const hideTimer = window.setTimeout(() => setIsMicTipVisible(false), 12200);

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, [isMobileLayout, conductorVoiceEnabled, teamId]);

  // The launch modal (now app-load global) can open Conductor with a chosen
  // prompt; fire it once on mount when provided.
  useEffect(() => {
    if (autoStartVoice) {
      startVoiceMode();
    } else if (initialPrompt) {
      handleSuggestionClick(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => (
    () => {
      isMountedRef.current = false;
      if (voiceSessionRef.current) {
        stopVoiceSession(voiceSessionRef.current);
        voiceSessionRef.current = null;
      }
    }
  ), []);

  useEffect(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (subscriptionThreadIdRef.current) {
      settleSubscriptionWaiters(subscriptionThreadIdRef.current, false);
    }
    subscriptionThreadIdRef.current = null;
    subscriptionConnectedRef.current = false;

    if (!activeThreadId) return;

    let hasConnected = false;
    subscriptionThreadIdRef.current = activeThreadId;
    subscriptionRef.current = subscribeToConductorChat(activeThreadId, {
      connected: () => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        subscriptionConnectedRef.current = true;
        settleSubscriptionWaiters(activeThreadId, true);
        if (hasConnected) loadMessages(activeThreadId, { showLoadingState: false });
        hasConnected = true;
      },
      disconnected: () => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        subscriptionConnectedRef.current = false;
        settleSubscriptionWaiters(activeThreadId, false);
      },
      rejected: () => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        subscriptionConnectedRef.current = false;
        settleSubscriptionWaiters(activeThreadId, false);
      },
      onChunk: (content, messageId) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        streamingMessageIdRef.current = messageId;
        streamBufferRef.current += content;
        if (streamFlushFrameRef.current == null) {
          streamFlushFrameRef.current = window.requestAnimationFrame(() => {
            streamFlushFrameRef.current = null;
            flushStreamBuffer();
          });
        }
      },
      onComplete: (message, completedContextUsage) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        resetStreamBuffer();
        pendingSmoothScrollRef.current = true;
        if (completedContextUsage) setContextUsage(completedContextUsage);
        // Bind this turn's tool calls + cards to the message so they survive the next turn's resetToolCalls().
        const turnToolCalls = completedToolCallsRef.current;
        const completedMessage = message ? {
          ...message,
          tool_calls: turnToolCalls,
          resource_cards: resourceCardsFromToolCalls(turnToolCalls)
        } : message;
        appendAssistantMessage(completedMessage);
        recordAssistantMessageInThreadList(activeThreadId, completedMessage);
        setThreadUsage((currentUsage) => advanceThreadUsage(currentUsage, message?.metadata?.llm));
        resetToolCalls();
        setStreamingMessage(null);
        pendingUserMessageIdRef.current = null;
        setIsSending(false);
      },
      onError: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        resetStreamBuffer();
        setStreamingMessage(null);
        appendAssistantMessage(data?.message);
        recordAssistantMessageInThreadList(activeThreadId, data?.message);
        resetToolCalls();
        pendingUserMessageIdRef.current = null;
        setIsSending(false);
      },
      onToolStarted: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        handleToolStarted(data);
      },
      onToolCompleted: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        handleToolCompleted(data);
      },
      onProcessingClassified: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        appendAssistantMessage(data.message);
        recordAssistantMessageInThreadList(activeThreadId, data.message);
        const foundOffer = (data.message?.metadata?.classifications || []).some((classification) => classification.document_type === 'offer');
        setUploadProgress((progress) => {
          if (!progress || progress.threadId !== activeThreadId) return progress;
          if (progress.jobId != null && progress.jobId !== data.job_id) return progress;
          return { ...progress, label: foundOffer ? 'Building the offer' : 'Reading the details' };
        });
      },
      onProcessingComplete: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        appendAssistantMessage(data.message);
        recordAssistantMessageInThreadList(activeThreadId, data.message);
      },
      onProcessingFailed: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        appendAssistantMessage(data.message);
        recordAssistantMessageInThreadList(activeThreadId, data.message);
      },
      received: (data) => {
        if (selectedThreadIdRef.current !== activeThreadId) return;
        if (data.type === 'voice_session_failed') {
          handleVoiceSessionFailure(data);
          return;
        }
        if (data.type === 'follow_ups') {
          setMessages((previous) => previous.map((message) => (message.id === data.message_id
            ? { ...message, metadata: { ...message.metadata, follow_ups: data.follow_ups } }
            : message)));
          return;
        }
        if (data.type === 'stream_start') {
          resetStreamBuffer();
          streamingMessageIdRef.current = data.message_id;
          setStreamingMessage({ id: data.message_id, content: '' });
          setToolCallMessageId(data.message_id);
          // Don't reset tool calls here: voice turns broadcast their tool activity
          // BEFORE stream_start, and a reloaded thread holds prior cards in the same
          // bucket — resetting would wipe both. onComplete/onError/loadMessages reset.
          if (data.user_message) {
            reconcileUserMessage(data.user_message);
            if (data.user_message.context_snapshot?.type === 'voice_transcript') {
              setVoiceActivity(null);
              recordUserMessageInThreadList(activeThreadId, data.user_message);
            }
          }
        }
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      settleSubscriptionWaiters(activeThreadId, false);
      if (subscriptionThreadIdRef.current === activeThreadId) {
        subscriptionThreadIdRef.current = null;
      }
      subscriptionConnectedRef.current = false;
    };
  }, [activeThreadId, handleToolStarted, handleToolCompleted, handleVoiceSessionFailure, reconcileUserMessage, resetToolCalls, resetStreamBuffer, flushStreamBuffer, settleSubscriptionWaiters, appendAssistantMessage, recordAssistantMessageInThreadList, recordUserMessageInThreadList]);

  const loadThreads = async () => {
    setIsLoadingThreads(true);
    try {
      const response = await fetch(VENUE_CONDUCTOR_THREADS_PATH, { headers });
      const data = await response.json();
      setThreads(data.threads || []);
    } catch (e) {
      toastError('Failed to load conversations');
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const loadMessages = async (threadId, { showLoadingState = true } = {}) => {
    const requestId = messageLoadRequestIdRef.current + 1;
    messageLoadRequestIdRef.current = requestId;

    if (showLoadingState) {
      setIsLoadingMessages(true);
    }
    resetToolCalls();
    try {
      const response = await fetch(venueConductorThreadPath(threadId), { headers });
      const data = await response.json();
      if (selectedThreadIdRef.current !== threadId || messageLoadRequestIdRef.current !== requestId) {
        return;
      }
      const nextMessages = data.messages || [];
      setMessages(messagesWithToolCalls(nextMessages, data.tool_calls || []));
      setThreadUsage(data.usage || null);
      setToolCallMessageId(null);
      setInitialToolCalls(nextMessages.length > 0 ? (data.tool_calls || []) : []);
      setContextUsage(data.context_usage || null);
      setSummaryBoundary(data.summary_boundary || null);
      setThreadHandoff(data.handoff || null);
    } catch (e) {
      if (selectedThreadIdRef.current === threadId && messageLoadRequestIdRef.current === requestId) {
        toastError('Failed to load messages');
      }
    } finally {
      if (showLoadingState && selectedThreadIdRef.current === threadId && messageLoadRequestIdRef.current === requestId) {
        setIsLoadingMessages(false);
      }
    }
  };

  const selectThread = (threadId) => {
    if (threadId === activeThreadId) {
      if (isOverlayThreads) setIsThreadSidebarOpen(false);
      return;
    }
    resetThreadView(threadId);
    setActiveThreadId(threadId);
    if (isOverlayThreads) {
      setIsThreadSidebarOpen(false);
    }
    loadMessages(threadId);
  };

  const createVenueConductorThread = async (threadEntryPoint = entryPoint) => {
    const response = await fetch(VENUE_CONDUCTOR_THREADS_PATH, {
      method: 'POST',
      headers,
      body: JSON.stringify({ entry_point: threadEntryPoint })
    });
    if (!response.ok) {
      throw new Error('Failed to create conversation');
    }

    const data = await response.json();
    const newThread = data.thread;
    setThreads(prev => [newThread, ...prev]);
    selectedThreadIdRef.current = newThread.id;
    setActiveThreadId(newThread.id);
    return newThread;
  };

  useEffect(() => {
    const handleInsightsChanged = (event) => setHasPendingInsight((event?.detail?.pendingCount || 0) > 0);
    window.addEventListener('conductorInsightsChanged', handleInsightsChanged);
    return () => window.removeEventListener('conductorInsightsChanged', handleInsightsChanged);
  }, []);

  const createThread = async () => {
    try {
      const newThread = await createVenueConductorThread();
      selectThread(newThread.id);
      if (isOverlayThreads) {
        setIsThreadSidebarOpen(false);
      }
      return newThread;
    } catch (e) {
      toastError('Failed to create conversation');
      return null;
    }
  };

  const startNewCue = () => {
    setIsManualPanelOpen(false);
    setCuesPanelView(null);
    resetThreadView(null);
    setActiveThreadId(null);
    if (isOverlayThreads) setIsThreadSidebarOpen(false);
    setPendingNewThreadPrompt(NEW_CUE_PROMPT);
  };

  const openCueThread = (threadId) => {
    setCuesPanelView(null);
    selectThread(threadId);
  };

  const toggleManualPanel = () => {
    setCuesPanelView(null);
    setIsManualPanelOpen((open) => !open);
  };

  const cuesPanelStartView = () => {
    if (!hasPendingInsight) return 'scheduled';
    const pending = insights.filter((insight) => insight.status === 'pending');
    if (pending.length > 0 && pending.every((insight) => insight.insight_type === 'cue')) return 'past';
    return 'opendate';
  };

  const toggleCuesPanel = () => {
    setIsManualPanelOpen(false);
    setCuesPanelView((view) => (view ? null : cuesPanelStartView()));
  };

  const renderNewMenu = (trigger) => (
    <Menu
      trigger={trigger}
      placement="bottom-end"
      minWidth={220}
      aria-label="Start something new"
      items={[
        { key: 'new-chat', label: 'New chat', icon: MessagePlusSquare, onClick: createThread },
        ...(conductorCuesEnabled ? [{ key: 'new-cue', label: 'New cue', description: 'Have Conductor do something later', icon: ClockStopwatch, onClick: startNewCue }] : [])
      ]}
    />
  );

  const deleteThread = async (threadId) => {
    try {
      await fetch(venueConductorThreadPath(threadId), {
        method: 'DELETE',
        headers
      });
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (activeThreadId === threadId) {
        resetThreadView(null);
        setActiveThreadId(null);
      }
    } catch (e) {
      toastError('Failed to delete conversation');
    }
  };

  const compactThread = async () => {
    if (!activeThreadId || isCompacting) return;
    const threadId = activeThreadId;
    setIsCompacting(true);
    try {
      const response = await fetch(venueConductorThreadCompactionPath(threadId), {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (!response.ok) {
        throw new Error('Failed to compact conversation');
      }
      const data = await response.json();
      if (selectedThreadIdRef.current !== threadId) return;
      if (data.context_usage) setContextUsage(data.context_usage);
      setSummaryBoundary(data.summary_boundary || null);
      toastSuccess('Earlier conversation summarized.');
    } catch (e) {
      toastError('Could not compact this conversation.');
    } finally {
      if (selectedThreadIdRef.current === threadId) setIsCompacting(false);
    }
  };

  const decideApprovals = async (toolCalls, decision) => {
    if (!activeThreadId || isSending) return;
    const threadId = activeThreadId;
    setIsSending(true);
    try {
      const streaming = await waitForSubscriptionConnection(threadId);
      for (const toolCall of toolCalls) {
        const response = await fetch(venueConductorThreadToolApprovalsPath(threadId), {
          method: 'POST',
          headers,
          body: JSON.stringify({ tool_call_id: toolCall.id, decision })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.errors?.[0] || 'Could not record that decision.');
        }
      }
      if (!streaming && selectedThreadIdRef.current === threadId) {
        await loadMessages(threadId, { showLoadingState: false });
        setIsSending(false);
      }
    } catch (e) {
      toastError(e.message || 'Could not record that decision.');
      if (selectedThreadIdRef.current === threadId) setIsSending(false);
    }
  };

  const startNewChatWithSummary = async () => {
    if (!activeThreadId || isCreatingHandoff) return;
    setIsCreatingHandoff(true);
    try {
      const response = await fetch(VENUE_CONDUCTOR_THREADS_PATH, {
        method: 'POST',
        headers,
        body: JSON.stringify({ source_thread_id: activeThreadId })
      });
      if (!response.ok) {
        throw new Error('Failed to start a new chat');
      }
      const data = await response.json();
      const newThread = data.thread;
      setThreads(prev => [newThread, ...prev]);
      selectThread(newThread.id);
      if (isOverlayThreads) setIsThreadSidebarOpen(false);
    } catch (e) {
      toastError('Could not start a new chat with a summary.');
    } finally {
      setIsCreatingHandoff(false);
    }
  };

  const sendMessage = async (contentOverride = null, { submitControl = null, threadEntryPoint = entryPoint } = {}) => {
    const nextContent = contentOverride === null ? inputValue : contentOverride;
    const userContent = nextContent.trim();
    if (!userContent || isSending) return;

    const activeComposerDirectives = composerDirectives;
    const content = buildMessageContent(userContent, activeComposerDirectives);
    setInputValue('');
    setComposerDirectives([]);
    setIsSending(true);

    const optimisticUserMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      display_name: 'You',
      created_at: new Date().toISOString()
    };
    pendingUserMessageIdRef.current = optimisticUserMessage.id;
    isPinnedToBottomRef.current = true;
    pendingSmoothScrollRef.current = true;
    setMessages((prev) => [...prev, optimisticUserMessage]);

    let threadId = activeThreadId;

    if (!threadId) {
      try {
        const newThread = await createVenueConductorThread(threadEntryPoint);
        threadId = newThread.id;
      } catch (e) {
        removePendingUserMessage();
        setInputValue(userContent);
        setComposerDirectives(activeComposerDirectives);
        toastError('Failed to start conversation');
        setIsSending(false);
        return;
      }
    }

    try {
      const useStreaming = await waitForSubscriptionConnection(threadId);
      const response = await fetch(venueConductorThreadMessagesPath(threadId), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          stream: useStreaming,
          attribution: {
            interface: conductorInterface,
            origin: contentOverride === null ? 'typed' : 'suggested_prompt',
            submit_control: submitControl
          }
        })
      });
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      const data = await response.json();
      if (selectedThreadIdRef.current !== threadId) {
        return;
      }

      if (data.user_message) {
        reconcileUserMessage(data.user_message);
        recordUserMessageInThreadList(threadId, data.user_message);
      }

      if (!useStreaming) {
        appendAssistantMessage(data.assistant_message);
        recordAssistantMessageInThreadList(threadId, data.assistant_message);
        await loadMessages(threadId, { showLoadingState: false });
        setIsSending(false);
      }
    } catch (e) {
      removePendingUserMessage();
      setInputValue(userContent);
      setComposerDirectives(activeComposerDirectives);
      toastError('Failed to send message');
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(null, { submitControl: 'enter' });
    }
  };

  const ensureActiveThreadId = async () => {
    if (activeThreadId) return activeThreadId;

    const newThread = await createVenueConductorThread();
    return newThread.id;
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const ALLOWED_TYPES = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.ms-excel.sheet.macroEnabled.12'
    ];
    const validFiles = Array.from(files).filter(
      (file) => ALLOWED_TYPES.includes(file.type) || /\.(pdf|jpe?g|png|webp|gif|csv|xlsx|xls|xlsm)$/i.test(file.name)
    );
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (validFiles.length === 0) {
      toastError('Please attach a PDF, image, spreadsheet or CSV.');
      return;
    }

    setIsUploadingFiles(true);
    try {
      const threadId = await ensureActiveThreadId();
      setUploadProgress({
        threadId,
        jobId: null,
        label: validFiles.length === 1 ? `Reading ${validFiles[0].name}` : `Reading ${validFiles.length} files`
      });

      const formData = new FormData();
      formData.append('ai_thread_id', threadId);
      validFiles.forEach((file) => formData.append('files[]', file));

      const response = await fetch(`/teams/${teamId}/document_processing_jobs`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = (errorData.errors || []).join(', ') || errorData.error || errorData.message;
        throw new Error(detail || `Upload failed (${response.status})`);
      }

      const data = await response.json();
      setUploadProgress((progress) => (
        progress?.threadId === threadId ? { ...progress, jobId: data.job_id } : progress
      ));
      await loadMessages(threadId, { showLoadingState: false });
    } catch (err) {
      setUploadProgress(null);
      toastError(err.message || 'Failed to upload offer');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  useEffect(() => {
    if (!pendingNewThreadPrompt) return;
    if (activeThreadId !== null) return;
    if (isSending) return;
    setPendingNewThreadPrompt(null);
    sendMessage(pendingNewThreadPrompt);
  }, [pendingNewThreadPrompt, activeThreadId, isSending, sendMessage]);

  const handleSuggestionClick = (suggestion) => {
    const bracketMatch = suggestion.match(/\[([^\]]+)\]/);
    setInputValue(suggestion);
    if (bracketMatch) {
      setTimeout(() => {
        inputRef.current?.focus();
        const start = bracketMatch.index;
        const end = start + bracketMatch[0].length;
        inputRef.current?.setSelectionRange(start, end);
      }, 0);
      return;
    }
    inputRef.current?.focus();
    sendMessage(suggestion);
  };

  const upsertComposerDirective = (directive) => {
    setComposerDirectives((currentDirectives) => [
      ...currentDirectives.filter((currentDirective) => currentDirective.id !== directive.id),
      directive
    ]);
    inputRef.current?.focus();
  };

  const removeComposerDirective = (directiveId) => {
    setComposerDirectives((currentDirectives) => (
      currentDirectives.filter((directive) => directive.id !== directiveId)
    ));
    inputRef.current?.focus();
  };

  const applyDateRange = () => {
    if (!dateRangeStart || !dateRangeEnd) {
      toastError('Choose a start and end date');
      return;
    }

    if (dateRangeStart > dateRangeEnd) {
      toastError('Start date must be before end date');
      return;
    }

    const selectedScope = DATE_RANGE_SCOPES.find((scope) => scope.value === dateRangeScope) || DATE_RANGE_SCOPES[0];
    upsertComposerDirective({
      id: 'date_range',
      kind: 'Date range',
      label: `${selectedScope.label}: ${dateRangeStart} to ${dateRangeEnd}`,
      prompt: selectedScope.prompt(dateRangeStart, dateRangeEnd)
    });
    setIsDateRangePanelOpen(false);
  };

  const handleExportReadyClick = () => {
    upsertComposerDirective(EXPORT_READY_DIRECTIVE);
  };

  const handleActionPromptClick = (prompt) => {
    setIsActionMenuOpen(false);
    handleSuggestionClick(prompt);
  };

  const dismissVoiceNudge = () => {
    setIsVoiceNudgeVisible(false);

    try {
      localStorage.setItem(voiceNudgeStorageKey(teamId), 'seen');
    } catch (e) {
      // Local storage is optional for the nudge.
    }
  };

  const dismissMicTip = () => {
    setIsMicTipVisible(false);

    try {
      localStorage.setItem(micTipStorageKey(teamId), 'seen');
    } catch (e) {
      // Local storage is optional for the tip.
    }
  };

  const ensureThreadForVoice = async () => {
    if (activeThreadId) {
      return activeThreadId;
    }

    const response = await fetch(VENUE_CONDUCTOR_THREADS_PATH, {
      method: 'POST',
      headers,
      body: JSON.stringify({ entry_point: entryPoint })
    });

    if (!response.ok) {
      throw new Error('Failed to start conversation');
    }

    const data = await response.json();
    const newThread = data.thread;
    setThreads(prev => [newThread, ...prev]);
    resetThreadView(newThread.id);
    selectedThreadIdRef.current = newThread.id;
    setActiveThreadId(newThread.id);

    if (isOverlayThreads) {
      setIsThreadSidebarOpen(false);
    }

    return newThread.id;
  };

  const handleVoiceDisconnected = () => {
    voiceSessionRef.current = null;
    voiceStartedAtRef.current = null;
    setVoiceStatus('idle');
    setVoiceElapsedSeconds(0);
    setVoiceActivity(null);
    setIsVoiceOrbMinimized(false);
  };

  const startVoiceMode = async () => {
    if (voiceStatus === 'connecting' || voiceStatus === 'listening') {
      return;
    }

    if (voiceStatus === 'paused' && voiceSessionRef.current) {
      setVoiceError(null);

      try {
        await setVoiceSessionMicrophoneEnabled(voiceSessionRef.current, true);
        voiceStartedAtRef.current = voiceStartedAtRef.current || Date.now();
        setVoiceStatus('listening');
      } catch (error) {
        setVoiceError(voiceErrorMessage(error));
      }

      return;
    }

    dismissVoiceNudge();
    dismissMicTip();
    setVoiceStatus('connecting');
    setVoiceElapsedSeconds(0);
    setVoiceError(null);
    setVoiceActivity(null);
    setIsVoiceOrbMinimized(false);

    try {
      if (voiceSessionRef.current) {
        await setVoiceSessionMicrophoneEnabled(voiceSessionRef.current, true);
        voiceStartedAtRef.current = Date.now();
        setVoiceStatus('listening');
        return;
      }

      const threadId = await ensureThreadForVoice();
      const voiceSession = await startVoiceSession({
        threadId,
        csrfToken,
        conductorInterface,
        onDisconnected: handleVoiceDisconnected,
        onTranscript: handleVoiceTranscript
      });

      if (!isMountedRef.current) {
        await stopVoiceSession(voiceSession);
        return;
      }

      voiceSessionRef.current = voiceSession;
      voiceStartedAtRef.current = Date.now();
      setVoiceStatus('listening');
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      voiceSessionRef.current = null;
      voiceStartedAtRef.current = null;
      setVoiceStatus('idle');
      setVoiceElapsedSeconds(0);
      setVoiceActivity(null);
      setIsVoiceOrbMinimized(false);
      setVoiceError(voiceErrorMessage(error));
    }
  };

  const pauseVoiceMode = async () => {
    const voiceSession = voiceSessionRef.current;
    if (!voiceSession) {
      setVoiceStatus('idle');
      setVoiceElapsedSeconds(0);
      return;
    }

    try {
      await setVoiceSessionMicrophoneEnabled(voiceSession, false);
      setVoiceStatus('paused');
    } catch (error) {
      setVoiceError(voiceErrorMessage(error));
    }
  };

  const endVoiceMode = async () => {
    const voiceSession = voiceSessionRef.current;
    voiceSessionRef.current = null;
    voiceStartedAtRef.current = null;
    setVoiceStatus('idle');
    setVoiceElapsedSeconds(0);
    setVoiceActivity(null);
    setIsVoiceOrbMinimized(false);

    if (voiceSession) {
      await stopVoiceSession(voiceSession);
    }
  };

  const toggleVoiceOrbMinimized = () => {
    setIsVoiceOrbMinimized((prev) => !prev);
  };

  const formatTimestamp = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const copyToClipboard = async (value, successMessage) => {
    if (!navigator.clipboard) {
      toastError('Unable to copy right now.');
      return;
    }

    try {
      const ok = await copyText(value);
      if (!ok) throw new Error('copy failed');
      toastSuccess(successMessage);
    } catch (e) {
      toastError('Unable to copy right now.');
    }
  };

  const renderUserAvatar = () => {
    if (currentUserAvatarUrl) {
      return (
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          backgroundColor: conductorTheme.border
        }}>
          <img
            src={currentUserAvatarUrl}
            alt=""
            aria-hidden="true"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </div>
      );
    }

    return (
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: conductorTheme.border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <User01 width={14} height={14} aria-hidden="true" style={{ color: conductorTheme.textFaint }} />
      </div>
    );
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const parsedUserMessage = isUser ? parseMessageContent(message.content) : null;

    return (
      <div key={message.id || index} style={{
        display: 'flex',
        gap: '12px',
        padding: '12px 20px',
        flexDirection: isUser ? 'row-reverse' : 'row'
      }}>
        {isUser ? renderUserAvatar() : !widgetLayout && <ConductorAvatar size={28} showBackground={false} />}
        <div style={{ flex: 1, minWidth: 0, maxWidth: isUser || !widgetLayout ? 'calc(100% - 50px)' : '100%' }}>
          {(isUser || !widgetLayout) && (
            <div style={{
              fontSize: '12px',
              color: conductorTheme.textSubtle,
              marginBottom: '4px',
              textAlign: isUser ? 'right' : 'left'
            }}>
              {isUser ? 'You' : 'Conductor'}
            </div>
          )}
          {isUser ? (
            <div style={{
              backgroundColor: conductorTheme.accentSurface,
              border: `1px solid ${conductorTheme.borderBlueStrong}`,
              color: conductorTheme.text,
              borderRadius: '12px',
              padding: '10px 14px',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              marginLeft: 'auto',
              maxWidth: isMobileLayout ? '92%' : '80%',
              width: 'fit-content'
            }}>
              {parsedUserMessage.directives.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginBottom: parsedUserMessage.content ? '8px' : 0,
                  justifyContent: 'flex-end'
                }}>
                  {parsedUserMessage.directives.map((directive) => (
                    <span
                      key={`${directive.kind}-${directive.label}`}
                      title={directive.prompt}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        border: `1px solid ${conductorTheme.accentBorder}`,
                        borderRadius: '999px',
                        backgroundColor: conductorTheme.accentSurfaceSoft,
                        color: conductorTheme.accentText,
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <DirectiveIcon directive={directive} size={11} />
                      {directive.kind}: {directive.label}
                    </span>
                  ))}
                </div>
              )}
              {parsedUserMessage.content}
            </div>
          ) : (
            <div className="venue-conductor-markdown" style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: conductorTheme.textStrong
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} transformLinkUri={transformMarkdownLinkUri}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {!isUser && (
            <ConductorResourceCards cards={createdCardsForMessages[message.id] || []} animateIn onOpenManualArticle={manualLinkHandler} />
          )}
          {!isUser && widgetLayout && (
            <ConductorResponseFooter
              content={message.content || ''}
              sources={sourcesForToolCalls(message.tool_calls)}
              followUps={message.id === lastAssistantMessageId && !isResponseStreaming
                ? message.metadata?.follow_ups || []
                : []}
              onCopy={copyToClipboard}
              onFollowUp={(text) => sendMessage(text)}
            />
          )}
          <div style={{
            fontSize: '11px',
            color: conductorTheme.textSubtle,
            marginTop: '4px',
            textAlign: isUser ? 'right' : 'left'
          }}>
            {formatTimestamp(message.created_at)}
          </div>
        </div>
      </div>
    );
  };

  const renderUploadProgress = () => {
    if (!uploadProgress || uploadProgress.threadId !== activeThreadId || streamingMessage) return null;

    return (
      <div style={{ display: 'flex', gap: '12px', padding: '12px 20px' }}>
        {!widgetLayout && <ConductorAvatar size={28} animated showBackground={false} />}
        <div style={{ flex: 1, minWidth: 0, padding: '6px 0' }}>
          <ConductorLoadingState label={uploadProgress.label} animation="constellation-orbit" />
        </div>
      </div>
    );
  };

  const renderStreamingMessage = () => {
    if (!streamingMessage) return null;

    const { stableBlocks, tail } = splitStreamingContent(streamingMessage.content);
    const visibleTail = withholdPartialTableRow(tail);
    const hasVisibleContent = stableBlocks.length > 0 || visibleTail.length > 0;

    return (
      <div style={{ display: 'flex', gap: '12px', padding: '12px 20px' }}>
        {!widgetLayout && <ConductorAvatar size={28} animated showBackground={false} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!widgetLayout && <div style={{ fontSize: '12px', color: conductorTheme.textSubtle, marginBottom: '4px' }}>Conductor</div>}
          {hasVisibleContent ? (
            <div className="venue-conductor-markdown" style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: conductorTheme.textStrong
            }}>
              {stableBlocks.map((block, index) => (
                <MarkdownBlock key={index} block={block} />
              ))}
              {visibleTail.length > 0 && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} transformLinkUri={transformMarkdownLinkUri}>
                  {visibleTail}
                </ReactMarkdown>
              )}
            </div>
          ) : widgetLayout ? (
            <div style={{ padding: '6px 0' }}>
              <ConductorLoadingState
                label={activeToolCalls.length > 0 ? `${activeToolCalls[activeToolCalls.length - 1].display_name}…` : 'Thinking'}
                animation={activeToolCalls.length > 0 ? 'constellation-orbit' : 'constellation-twinkle'}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '4px', padding: '8px 0' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: conductorTheme.textSubtle, animation: 'venue-conductor-bounce 1s infinite' }} />
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: conductorTheme.textSubtle, animation: 'venue-conductor-bounce 1s infinite 0.2s' }} />
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: conductorTheme.textSubtle, animation: 'venue-conductor-bounce 1s infinite 0.4s' }} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderToolCalls = () => {
    if (widgetLayout) return null;

    if (activeToolCalls.length === 0) return null;

    return (
      <div style={{ display: 'flex', gap: '12px', padding: '12px 20px' }}>
        <ConductorAvatar size={28} animated={activeToolCalls.length > 0} showBackground={false} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: conductorTheme.textSubtle, marginBottom: '4px' }}>Conductor</div>
          {activeToolCalls.map((toolCall, index) => (
            <ToolCallIndicator key={`${toolCall.id || toolCall.tool_name}-${index}`} toolCall={toolCall} isActive={true} />
          ))}
        </div>
      </div>
    );
  };

  const renderVoiceStage = () => {
    if (voiceStatus !== 'connecting' && voiceStatus !== 'listening' && voiceStatus !== 'paused') {
      return null;
    }

    const isConnecting = voiceStatus === 'connecting';
    const isListening = voiceStatus === 'listening';
    const isPaused = voiceStatus === 'paused';
    const activeTool = activeToolCalls.length > 0
      ? activeToolCalls[activeToolCalls.length - 1]
      : null;
    const activeToolLabel = activeTool ? (activeTool.display_name || 'Working') : null;
    // While a tool is running, surface what Conductor is doing instead of "Thinking".
    const listeningStatusText = activeToolLabel || {
      speaking: 'Hearing you',
      transcribing: 'Transcribing',
      processing: 'Thinking'
    }[voiceActivity];
    const statusText = voiceStatus === 'connecting'
      ? 'Connecting microphone'
      : `${listeningStatusText || (isPaused ? 'Muted' : 'Listening')}${isPaused && listeningStatusText ? ' · muted' : ''}`;
    const hasActiveVoiceSignal = isListening || Boolean(voiceActivity);
    const voiceOrbClassName = [
      'venue-conductor-voice-orb',
      hasActiveVoiceSignal ? 'venue-conductor-voice-orb--active' : '',
      isPaused ? 'venue-conductor-voice-orb--paused' : ''
    ].filter(Boolean).join(' ');
    const orbSize = isMobileLayout ? 156 : 184;
    const innerOrbSize = isMobileLayout ? 72 : 82;

    const renderStatusPill = () => (
      <div
        aria-live="polite"
        style={{
          minWidth: isVoiceOrbMinimized ? '0' : '210px',
          border: isVoiceOrbMinimized ? 'none' : `1px solid ${conductorTheme.borderMuted}`,
          borderRadius: '8px',
          backgroundColor: isVoiceOrbMinimized ? 'transparent' : conductorTheme.floatingSurface,
          boxShadow: isVoiceOrbMinimized ? 'none' : '0 10px 30px rgba(15, 23, 42, 0.1)',
          padding: isVoiceOrbMinimized ? '0' : '7px 11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}
      >
        <span
          data-voice-status-text="true"
          style={{
            flex: 1,
            minWidth: 0,
            color: conductorTheme.textSlate,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {statusText}
        </span>
        {(isListening || isPaused) && (
          <span
            aria-label="Voice elapsed time"
            style={{
              color: conductorTheme.textMuted,
              fontSize: '12px',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.2
            }}
          >
            {formatVoiceElapsed(voiceElapsedSeconds)}
          </span>
        )}
      </div>
    );

    const renderOrb = (isCompact = false) => (
      <div
        data-voice-orb="true"
        className={voiceOrbClassName}
        role="img"
        aria-label={statusText}
        style={{
          width: isCompact ? '44px' : `${orbSize}px`,
          height: isCompact ? '44px' : `${orbSize}px`,
          boxShadow: isCompact ? conductorTheme.voiceOrbCompactShadow : undefined,
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: isCompact ? '24px' : `${innerOrbSize}px`,
            height: isCompact ? '24px' : `${innerOrbSize}px`,
            borderRadius: '50%',
            border: `1px solid ${conductorTheme.voiceOrbInnerBorder}`,
            backgroundColor: conductorTheme.voiceOrbInnerSurface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isPaused ? conductorTheme.voiceOrbPausedIcon : conductorTheme.voiceOrbIcon,
            boxShadow: conductorTheme.voiceOrbInnerShadow
          }}
        >
          {isConnecting ? (
            <Spinner size={isCompact ? 13 : 24} color="currentColor" />
          ) : isPaused ? (
            <MicrophoneOff01 width={isCompact ? 13 : 24} height={isCompact ? 13 : 24} aria-hidden="true" />
          ) : (
            <Microphone01 width={isCompact ? 13 : 24} height={isCompact ? 13 : 24} aria-hidden="true" />
          )}
        </div>
      </div>
    );

    const renderMuteButton = () => {
      if (!isListening && !isPaused) {
        return null;
      }

      return (
        <Button
          variant="secondary"
          size="sm"
          icon={isPaused ? Microphone01 : MicrophoneOff01}
          aria-label={isPaused ? 'Unmute microphone' : 'Mute microphone'}
          aria-pressed={isPaused}
          title={isPaused ? 'Unmute microphone' : 'Mute microphone'}
          onClick={isPaused ? startVoiceMode : pauseVoiceMode}
          className="venue-conductor-ui-icon-button"
        />
      );
    };

    const renderMinimizeButton = () => (
      <Button
        variant="secondary"
        size="sm"
        icon={isVoiceOrbMinimized ? Maximize01 : Minimize01}
        aria-label={isVoiceOrbMinimized ? 'Expand voice orb' : 'Minimize voice orb'}
        title={isVoiceOrbMinimized ? 'Expand voice orb' : 'Minimize voice orb'}
        onClick={toggleVoiceOrbMinimized}
        className="venue-conductor-ui-icon-button"
      />
    );

    // Explicit hang-up so it's obvious how to end the call (not just mute).
    // Icon-only in the compact pill so it doesn't crowd out the status text;
    // labeled ("End") in the full voice stage.
    const renderEndButton = () => (
      <Button
        variant="destructive"
        size={isVoiceOrbMinimized ? 'sm' : 'md'}
        icon={PhoneX}
        aria-label="End voice call"
        title="End voice call"
        onClick={endVoiceMode}
      >
        {isVoiceOrbMinimized ? null : 'End'}
      </Button>
    );

    if (isVoiceOrbMinimized) {
      return (
        <div
          data-voice-stage="true"
          data-immersive-voice-stage="true"
          data-mobile-voice-stage={isMobileLayout ? 'true' : undefined}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            background: 'transparent'
          }}
        >
          <div
            data-voice-compact-stage="true"
            style={{
              position: 'absolute',
              top: isMobileLayout ? '14px' : '18px',
              right: isMobileLayout ? '14px' : '20px',
              maxWidth: isMobileLayout ? 'calc(100% - 28px)' : '420px',
              border: `1px solid ${conductorTheme.borderMuted}`,
              borderRadius: '10px',
              backgroundColor: conductorTheme.floatingSurfaceStrong,
              boxShadow: '0 16px 38px rgba(15, 23, 42, 0.14)',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              pointerEvents: 'auto'
            }}
          >
            {renderOrb(true)}
            <div style={{ minWidth: 0 }}>
              {renderStatusPill()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {renderMuteButton()}
              {renderMinimizeButton()}
              {renderEndButton()}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        data-voice-stage="true"
        data-immersive-voice-stage="true"
        data-mobile-voice-stage={isMobileLayout ? 'true' : undefined}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobileLayout ? '18px 16px 24px' : '26px 28px 32px',
          pointerEvents: 'none',
          // Immersive voice mode paints an opaque cover over the chat.
          // Minimizing is the only thing that reveals the chat underneath.
          background: conductorTheme.voiceStageBackground,
          transition: 'background 0.2s ease'
        }}
      >
        <div style={{
          width: '100%',
          maxWidth: isMobileLayout ? '380px' : '460px',
          minHeight: isMobileLayout ? '360px' : '410px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobileLayout ? '18px' : '20px',
          pointerEvents: 'auto'
        }}>
          {renderStatusPill()}
          {renderOrb(false)}
          {activeToolLabel && (
            <div data-voice-tool-caption="true" style={{ maxWidth: '100%' }}>
              <ConductorLoadingState label={activeToolLabel} animation="constellation-orbit" />
            </div>
          )}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}>
              {renderMuteButton()}
              {renderMinimizeButton()}
            </div>
            {renderEndButton()}
          </div>
        </div>
      </div>
    );
  };

  const renderInsightCard = (insight) => {
    return (
      <div
        key={insight.id}
        data-testid="conductor-insight-card"
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          textAlign: 'left',
          width: '100%',
          padding: '14px 16px',
          border: `1px solid ${conductorTheme.borderBlue}`,
          borderRadius: '14px',
          backgroundColor: conductorTheme.surface,
          boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
          animation: 'conductor-tip-in 200ms ease-out'
        }}
      >
        <ConductorAvatar size={28} animated showBackground={false} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: conductorTheme.textStrong, lineHeight: 1.4 }}>
            {insight.title}
          </div>
          {insight.body && (
            <div style={{ marginTop: '4px', fontSize: '13px', color: conductorTheme.textSlate, lineHeight: 1.5 }}>
              {insight.body}
            </div>
          )}
          {insight.prompt && (
            <button
              type="button"
              onClick={() => sendMessage(insight.prompt, { threadEntryPoint: 'insight' })}
              style={{
                marginTop: '10px',
                border: 'none',
                borderRadius: '999px',
                backgroundColor: conductorTheme.accentSurface,
                color: conductorTheme.accentText,
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Look into this
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss insight"
          onClick={() => dismissInsight(insight)}
          style={{
            border: 'none',
            background: 'transparent',
            color: conductorTheme.iconMuted,
            cursor: 'pointer',
            padding: '2px',
            fontSize: '13px',
            flexShrink: 0
          }}
        >
          <XClose width={13} height={13} aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderInsightCards = () => {
    if (insights.length === 0) return null;

    const visible = insightsExpanded ? insights : insights.slice(0, VISIBLE_INSIGHT_LIMIT);
    const hiddenCount = insights.length - visible.length;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: '100%',
          maxWidth: '520px',
          margin: isMobileLayout ? '0 0 18px' : '26px 0 0'
        }}
      >
        {visible.map(renderInsightCard)}
        {(hiddenCount > 0 || insightsExpanded) && (
          <button
            type="button"
            onClick={() => setInsightsExpanded((expanded) => !expanded)}
            style={{
              alignSelf: 'center',
              border: 'none',
              background: 'transparent',
              color: conductorTheme.textSubtle,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '2px 6px'
            }}
          >
            {insightsExpanded ? 'Show less' : `+${hiddenCount} more`}
          </button>
        )}
      </div>
    );
  };

  const voiceSpeakingLine = streamingMessage?.content
    ? streamingMessage.content.trim().split(/(?<=[.!?])\s+/).filter(Boolean).pop()
    : null;
  const voiceActiveToolName = activeToolCalls[0]?.display_name || null;

  useEffect(() => {
    onVoiceStateChange?.({
      status: voiceStatus,
      activity: voiceActivity,
      activeToolName: voiceActiveToolName,
      speakingLine: voiceSpeakingLine,
      error: voiceError
    });
  }, [onVoiceStateChange, voiceStatus, voiceActivity, voiceActiveToolName, voiceSpeakingLine, voiceError]);

  const handledVoiceEndRequestIdRef = useRef(voiceEndRequestId);
  useEffect(() => {
    if (handledVoiceEndRequestIdRef.current === voiceEndRequestId) return;

    handledVoiceEndRequestIdRef.current = voiceEndRequestId;
    endVoiceMode();
  }, [voiceEndRequestId]);

  const renderEmptyState = () => {
    const emptyStatePrompts = buildEmptyStatePrompts(effectivePromptContext || {});
    const promptsToShow = isMobileLayout
      ? emptyStatePrompts.slice(0, 4)
      : emptyStatePrompts;

    // On mobile, lead with voice: a big "Try voice mode" mic instead of a wall of
    // prompt chips. Falls back to prompts when the team has no voice access.
    const showMobileVoiceCta = isMobileLayout && conductorVoiceEnabled;
    const isVoiceStarting = voiceStatus === 'connecting';

    // Conductor logo/name pinned to the top of the screen (just below the app
    // header); the primary CTA gets centered in the space below it.
    const brand = (
      <>
        <ConductorAvatar size={isMobileLayout ? 40 : 48} animated showBackground={false} />
        <h3 style={{
          fontSize: isMobileLayout ? '16px' : '18px',
          fontWeight: 600,
          color: conductorTheme.textStrong,
          margin: isMobileLayout ? '10px 0 0' : '16px 0 8px'
        }}>
          Conductor
        </h3>
        {!showMobileVoiceCta && (
          <p style={{
            fontSize: isMobileLayout ? '13px' : '14px',
            color: conductorTheme.textFaint,
            maxWidth: '400px',
            lineHeight: '1.55',
            margin: isMobileLayout ? '6px 0 0' : '0'
          }}>
            Ask about events, ticket sales, financials, offers, or fan analytics across your venues.
          </p>
        )}
      </>
    );

    // The big CTA is the actual voice orb (idle/animated) — tapping it starts the
    // session, transitioning straight into the live voice stage's orb.
    const voiceCta = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button
          type="button"
          onClick={startVoiceMode}
          disabled={isVoiceStarting}
          aria-label="Try voice mode"
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            borderRadius: '50%',
            cursor: isVoiceStarting ? 'default' : 'pointer',
            opacity: isVoiceStarting ? 0.85 : 1
          }}
        >
          <div
            className="venue-conductor-voice-orb venue-conductor-voice-orb--active"
            style={{ width: '156px', height: '156px' }}
          >
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              border: `1px solid ${conductorTheme.voiceOrbInnerBorder}`,
              backgroundColor: conductorTheme.voiceOrbInnerSurface,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: conductorTheme.voiceOrbIcon,
              boxShadow: conductorTheme.voiceOrbInnerShadow
            }}>
              {isVoiceStarting ? (
                <Spinner size={24} color="currentColor" />
              ) : (
                <Microphone01 width={24} height={24} aria-hidden="true" />
              )}
            </div>
          </div>
        </button>
        <div style={{ marginTop: '26px', fontSize: '16px', fontWeight: 700, color: conductorTheme.textStrong }}>
          {isVoiceStarting ? 'Connecting…' : 'Try voice mode'}
        </div>
        <div style={{ marginTop: '4px', fontSize: '12px', color: conductorTheme.textFaint }}>
          Tap the mic to start talking.
        </div>
      </div>
    );

    const promptsCta = (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobileLayout ? '6px' : '8px',
        marginTop: isMobileLayout ? '0' : '24px',
        justifyContent: 'center',
        maxWidth: '500px'
      }}>
        {promptsToShow.map((suggestion) => (
          <Button
            key={suggestion}
            variant="secondary"
            size="sm"
            onClick={() => handleSuggestionClick(suggestion)}
            style={{ whiteSpace: 'normal', textAlign: 'center' }}
          >
            {suggestion}
          </Button>
        ))}
        <Button variant="link-color" size="sm" onClick={() => setShowPromptsPanel(true)}>
          Browse all prompts <ArrowRight width={12} height={12} aria-hidden="true" style={{ verticalAlign: '-2px' }} />
        </Button>
      </div>
    );

    const cta = showMobileVoiceCta ? voiceCta : promptsCta;

    if (condensedLayout) {
      const visibleEmptyStatePrompts = showAllEmptyStatePrompts && !compactLayout
        ? emptyStatePrompts
        : emptyStatePrompts.slice(0, compactLayout ? 3 : 5);
      return (
        <div className={`venue-conductor-ui-empty${compactLayout ? '' : ' is-docked'}`}>
          {!compactLayout && (
            <Conductor className="venue-conductor-motion-stage" width={40} height={40} />
          )}
          <h2 className="venue-conductor-motion-stage" style={{ '--motion-stage': 3 }}>
            {compactLayout ? 'What can I help you with?' : 'Conductor'}
          </h2>
          <p className="venue-conductor-motion-stage" style={{ '--motion-stage': 4 }}>
            {compactLayout
              ? 'Ask me a quick question here or expand when you need more room'
              : 'Ask about events, ticket sales, financials, offers, or fan analytics across your venues.'}
          </p>
          {renderInsightCards()}
          <div className="venue-conductor-ui-prompts">
            {visibleEmptyStatePrompts.map((suggestion, index) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="venue-conductor-ui-prompt venue-conductor-motion-stage"
                style={{ '--motion-stage': 5 + index }}
              >
                {React.createElement(EMPTY_STATE_PROMPT_ICONS[index], { width: 16, height: 16, 'aria-hidden': 'true' })}
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
          {!compactLayout && (
            <button
              type="button"
              className="venue-conductor-ui-browse"
              aria-expanded={showAllEmptyStatePrompts}
              onClick={() => setShowAllEmptyStatePrompts((showAll) => !showAll)}
            >
              {showAllEmptyStatePrompts ? 'Show fewer prompts' : 'Browse all prompts'}
              {' '}
              {showAllEmptyStatePrompts ? <ChevronUp width={12} height={12} aria-hidden="true" /> : <ChevronDown width={12} height={12} aria-hidden="true" />}
            </button>
          )}
        </div>
      );
    }

    if (isMobileLayout) {
      // Mobile: voice mode is the focus — no logo/heading, just the CTA centered.
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 16px 22px',
          textAlign: 'center',
          minHeight: 0
        }}>
          {renderInsightCards()}
          {cta}
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        {brand}
        {renderInsightCards()}
        {cta}
      </div>
    );
  };

  const lastMessage = messages[messages.length - 1];
  const lastUserMessage = [...messages].reverse().find((message) => message.role === 'user');
  const lastUserMessageAt = lastUserMessage?.created_at || null;
  const lastAssistantMessageId = [...messages].reverse().find((message) => message.role === 'assistant')?.id || null;
  const pendingApprovals =
    !isSending &&
    streamingMessage == null &&
    lastMessage?.role === 'assistant'
      ? pendingApprovalsFromToolCalls(
        [...completedToolCalls, ...(lastMessage.tool_calls || [])],
        lastUserMessageAt
      )
      : [];

  const isResponseStreaming = isSending || streamingMessage != null;
  const headroomActionsDisabledReason =
    isResponseStreaming ? 'Wait for the current response to finish.'
      : isUploadingFiles ? 'Wait for the upload to finish.'
        : pendingApprovals.length > 0 ? 'Respond to the pending action first.'
          : undefined;
  const headroomActionsDisabled = headroomActionsDisabledReason != null;

  const renderSummarizedMarker = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: '12px 20px 4px',
        color: conductorTheme.textMuted
      }}
    >
      <span style={{ flex: 1, height: '1px', backgroundColor: conductorTheme.border }} />
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        fontWeight: 600,
        whiteSpace: 'nowrap'
      }}>
        <LayersThree01 width={11} height={11} aria-hidden="true" />
        Earlier conversation summarized
      </span>
      <span style={{ flex: 1, height: '1px', backgroundColor: conductorTheme.border }} />
    </div>
  );

  const renderConfirmActionBar = () => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '0 20px 12px 60px' }}>
      <button
        type="button"
        onClick={() => decideApprovals(pendingApprovals, 'approved')}
        disabled={isSending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          fontWeight: 600,
          padding: '6px 16px',
          borderRadius: '16px',
          border: `1px solid ${conductorTheme.accentBorder}`,
          backgroundColor: conductorTheme.accentSurface,
          color: conductorTheme.accentText,
          cursor: 'pointer'
        }}
      >
        <Check width={13} height={13} aria-hidden="true" />
        Yes, do it
      </button>
      <button
        type="button"
        onClick={() => decideApprovals(pendingApprovals, 'denied')}
        disabled={isSending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          fontWeight: 600,
          padding: '6px 16px',
          borderRadius: '16px',
          border: `1px solid ${conductorTheme.border}`,
          backgroundColor: conductorTheme.surface,
          color: conductorTheme.textSoft,
          cursor: 'pointer'
        }}
      >
        No, cancel
      </button>
    </div>
  );

  const shouldAnchorToolCalls =
    (activeToolCalls.length > 0 || completedToolCalls.length > 0) &&
    toolCallMessageId != null;

  const anchoredToolCallMessageIndex = shouldAnchorToolCalls
    ? messages.findIndex((message) => message.id === toolCallMessageId)
    : -1;

  const messagesBeforeToolCalls = anchoredToolCallMessageIndex >= 0
    ? messages.slice(0, anchoredToolCallMessageIndex)
    : messages;

  const anchoredToolCallMessage = anchoredToolCallMessageIndex >= 0
    ? messages[anchoredToolCallMessageIndex]
    : null;

  const messagesAfterToolCalls = anchoredToolCallMessageIndex >= 0
    ? messages.slice(anchoredToolCallMessageIndex + 1)
    : [];

  const hasDateRangeDirective = composerDirectives.some((directive) => directive.id === 'date_range');
  const hasExportReadyDirective = composerDirectives.some((directive) => directive.id === EXPORT_READY_DIRECTIVE.id);
  const isVoiceStageVisible = voiceStatus === 'connecting' || voiceStatus === 'listening' || voiceStatus === 'paused';
  const shouldDimMessagesForVoice = isVoiceStageVisible && !isVoiceOrbMinimized;

  const showMcpPanel = existingAiIntegration && !isMcpPanelDismissed && !isMobileLayout && !compactLayout;
  const threadToolCalls = threadToolCallsFrom(messages, completedToolCalls);
  const { created: threadResourceCards, referenced: threadReferencedCards } = classifiedResourceCards(threadToolCalls);
  const createdCardsForMessages = createdCardsByMessage(messages, threadToolCalls);
  const renderManualToggle = ({ iconOnly = false } = {}) => {
    if (!helpCenterEnabled) return null;
    const shared = {
      type: 'button',
      'aria-label': isManualPanelOpen ? 'Back to chat' : 'Open the Help Center',
      'aria-pressed': isManualPanelOpen,
      title: 'Browse the Help Center without leaving the chat',
      onClick: toggleManualPanel
    };
    if (iconOnly) {
      return <Button {...shared} variant="tertiary" size="sm" icon={LifeBuoy01} />;
    }
    return (
      <Button
        {...shared}
        variant={isManualPanelOpen ? 'secondary-color' : 'secondary'}
        size="sm"
        icon={LifeBuoy01}
        className="venue-conductor-btn"
      >
        Help
      </Button>
    );
  };
  const renderCuesToggle = ({ iconOnly = false, inDockHeader = false } = {}) => {
    if (!conductorCuesEnabled) return null;
    const isOpen = Boolean(cuesPanelView);
    const toggleClassName = inDockHeader ? 'venue-conductor-ui-text-button' : 'venue-conductor-btn';
    const button = (
      <Button
        type="button"
        variant={iconOnly || inDockHeader ? 'tertiary' : (isOpen ? 'secondary-color' : 'secondary')}
        size="sm"
        icon={ClockStopwatch}
        className={iconOnly ? 'venue-conductor-ui-icon-button is-ghost' : toggleClassName}
        aria-label={isOpen ? 'Back to chat' : 'Open your cues'}
        aria-pressed={isOpen}
        title="Your cues, and cues from Opendate"
        onClick={toggleCuesPanel}
      >
        {iconOnly ? null : 'Cues'}
      </Button>
    );
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        {button}
        {hasPendingInsight && (
          <span
            data-testid="conductor-cues-dot"
            aria-label="New cue from Opendate"
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              width: '8px',
              height: '8px',
              borderRadius: '999px',
              backgroundColor: conductorTheme.accentText,
              boxShadow: `0 0 0 2px ${conductorTheme.surface}`,
              pointerEvents: 'none'
            }}
          />
        )}
      </span>
    );
  };
  const activeThread = threads.find((thread) => thread.id === activeThreadId) || null;
  const widgetLayout = condensedLayout;
  const composerToken = widgetLayout ? /^([@/])(\S*)$/.exec(inputValue) : null;
  const isPromptMenuOpen = widgetLayout && (isActionMenuOpen || Boolean(composerToken));
  const clearComposerToken = () => {
    if (composerToken) setInputValue('');
  };
  const promptMenuSections = [
    {
      key: 'add',
      title: 'Add',
      rows: [
        { key: 'attach', name: 'Photos & files', desc: 'Offers, ticket counts, spreadsheets', icon: Paperclip, onSelect: () => { clearComposerToken(); handleAttachClick(); } },
        { key: 'date-range', name: 'Date range', desc: 'Scope the answer to dates', icon: CalendarDate, checked: hasDateRangeDirective, onSelect: () => { clearComposerToken(); setIsDateRangePanelOpen(true); } },
        { key: 'export-ready', name: 'Export-ready', desc: 'Return a table you can export', icon: File06, checked: hasExportReadyDirective, onSelect: () => { clearComposerToken(); handleExportReadyClick(); } }
      ]
    },
    {
      key: 'connect',
      title: 'Connect your AI',
      rows: (existingAiIntegration?.providers || []).map((provider) => ({
        key: `connect-${provider.id}`,
        name: provider.name,
        desc: 'Set up in chat',
        image: ['claude', 'chatgpt'].includes(provider.id) ? `/conductor/connect-${provider.id}.svg` : null,
        icon: Dataflow01,
        onSelect: () => {
          setInputValue('');
          sendMessage(`Walk me through connecting Opendate to ${provider.name}.`);
        }
      })).concat(conductorTextingEnabled ? [{
        key: 'connect-texting',
        name: 'Text Conductor',
        desc: 'Set up in chat',
        icon: Phone01,
        onSelect: () => {
          setInputValue('');
          sendMessage('Walk me through texting Conductor from my phone.');
        }
      }] : [])
    },
    {
      key: 'actions',
      title: 'Take action',
      rows: ACTION_PROMPTS.map((prompt, index) => ({
        key: `action-${index}`,
        name: prompt,
        icon: Zap,
        onSelect: () => handleActionPromptClick(prompt)
      }))
    }
  ].filter((section) => {
    if (composerToken?.[1] === '/') return section.key !== 'connect';
    if (composerToken?.[1] === '@') return section.key !== 'actions';
    return true;
  });
  const renderActionMenu = () => (
    <div style={{
      position: 'absolute',
      bottom: 'calc(100% + 8px)',
      left: 0,
      width: isMobileLayout ? 'min(78vw, 280px)' : '320px',
      backgroundColor: conductorTheme.surface,
      border: `1px solid ${conductorTheme.borderBlue}`,
      borderRadius: '14px',
      boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
      padding: isMobileLayout ? '6px' : '8px',
      zIndex: 3
    }}>
      <button
        type="button"
        className="venue-conductor-menu-item"
        onClick={() => {
          setIsDateRangePanelOpen(true);
          setIsActionMenuOpen(false);
        }}
        style={composerMenuItemStyle}
      >
        <CalendarDate width={14} height={14} aria-hidden="true" style={composerMenuIconStyle} />
        Date range
        {hasDateRangeDirective && <Check width={14} height={14} aria-hidden="true" style={composerMenuCheckStyle} />}
      </button>
      <button
        type="button"
        className="venue-conductor-menu-item"
        onClick={() => {
          handleExportReadyClick();
          setIsActionMenuOpen(false);
        }}
        style={composerMenuItemStyle}
      >
        <File06 width={14} height={14} aria-hidden="true" style={composerMenuIconStyle} />
        Export-ready
        {hasExportReadyDirective && <Check width={14} height={14} aria-hidden="true" style={composerMenuCheckStyle} />}
      </button>
      <div style={{ borderTop: `1px solid ${conductorTheme.border}`, margin: '6px 4px' }} />
      <div style={{ padding: '2px 10px 4px', fontSize: '10px', fontWeight: 700, color: conductorTheme.textFaint, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        <Zap width={10} height={10} aria-hidden="true" style={{ marginRight: '5px' }} />
        Take action
      </div>
      {ACTION_PROMPTS.map((prompt) => (
        <button
          key={prompt}
          type="button"
          className="venue-conductor-menu-item"
          onClick={() => handleActionPromptClick(prompt)}
          style={{
            display: 'block',
            width: '100%',
            border: 'none',
            color: conductorTheme.textStrong,
            textAlign: 'left',
            borderRadius: '10px',
            padding: '9px 10px',
            fontSize: '12px',
            lineHeight: '1.35',
            cursor: 'pointer'
          }}
        >
          {prompt}
        </button>
      ))}
    </div>
  );

  return (
    <ManualLinkContext.Provider value={manualLinkHandler}>
    <div style={{
      display: 'flex',
      position: 'relative',
      height: containerHeight,
      backgroundColor: conductorTheme.surface,
      borderRadius: isMobileLayout || condensedLayout ? '0' : '8px',
      overflow: 'hidden',
      border: isMobileLayout || compactLayout ? 'none' : `1px solid ${conductorTheme.border}`,
      touchAction: isMobileLayout ? 'manipulation' : undefined
    }}>
      {renderToastContainer && <ToastContainer icon={false} />}
      <style>{MARKDOWN_STYLES}</style>
      <style>{VOICE_STAGE_STYLES}</style>
      <style>{CONDUCTOR_DISCOVERY_STYLES}</style>

      {isOverlayThreads && isThreadSidebarOpen && (
        <button
          type="button"
          aria-label="Close conversations"
          onClick={() => setIsThreadSidebarOpen(false)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 4,
            border: 'none',
            backgroundColor: 'rgba(17, 24, 39, 0.32)',
            cursor: 'pointer'
          }}
        />
      )}

      {compactLayout && isThreadSidebarOpen && (
        <section
          id="venue-conductor-compact-thread-history"
          className="venue-conductor-compact-thread-history"
          data-testid="compact-thread-history"
          role="dialog"
          aria-label="Recent chats"
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            right: '8px',
            maxHeight: 'calc(100% - 16px)',
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${conductorTheme.borderBlue}`,
            borderRadius: '12px',
            backgroundColor: conductorTheme.surface,
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.24)',
            animation: 'conductor-compact-history-in 140ms ease-out'
          }}
        >
          <div style={{
            minHeight: '44px',
            padding: '8px 10px 8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderBottom: `1px solid ${conductorTheme.border}`,
            backgroundColor: conductorTheme.surfaceInset
          }}>
            <ClockRewind width={16} height={16} aria-hidden="true" style={{ color: conductorTheme.accentText, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: conductorTheme.textStrong }}>
                Recent chats
              </div>
              <div style={{ fontSize: '11px', color: conductorTheme.textSubtle }}>
                Switch without leaving quick chat
              </div>
            </div>
            <Button
              variant="tertiary"
              size="sm"
              icon={XClose}
              className="venue-conductor-compact-thread-close"
              aria-label="Close recent chats"
              onClick={() => setIsThreadSidebarOpen(false)}
            />
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px' }}>
            {isLoadingThreads ? (
              <div style={{ padding: '20px', textAlign: 'center', color: conductorTheme.textSubtle, fontSize: '12px' }}>
                Loading chats…
              </div>
            ) : threads.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: conductorTheme.textSubtle, fontSize: '12px' }}>
                No previous chats yet
              </div>
            ) : (
              threads.map((thread) => {
                const isActive = activeThreadId === thread.id;
                return (
                  <button
                    key={thread.id}
                    type="button"
                    className="venue-conductor-compact-thread-option"
                    data-testid="compact-thread-option"
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => selectThread(thread.id)}
                    style={{
                      width: '100%',
                      minHeight: '48px',
                      padding: '8px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? conductorTheme.accentSurfaceStrong : 'transparent',
                      color: conductorTheme.textStrong,
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        overflow: 'hidden',
                        color: conductorTheme.textStrong,
                        fontSize: '12px',
                        fontWeight: isActive ? 700 : 600,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {thread.title || 'New chat'}
                      </div>
                      <div style={{ marginTop: '2px', color: conductorTheme.textSubtle, fontSize: '10px' }}>
                        {thread.message_count || 0} messages
                        {thread.last_message_at && ` · ${formatTimestamp(thread.last_message_at)}`}
                      </div>
                    </div>
                    {isActive && (
                      <Check width={14} height={14} aria-hidden="true" style={{ color: conductorTheme.accentText, flexShrink: 0 }} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </section>
      )}

      {!compactLayout && <div style={{
          width: isMobileLayout ? 'min(86vw, 320px)' : '280px',
          position: isOverlayThreads ? 'absolute' : 'relative',
          inset: isOverlayThreads ? '0 auto 0 0' : 'auto',
          borderRight: `1px solid ${conductorTheme.border}`,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: conductorTheme.surfaceInset,
          flexShrink: 0,
          zIndex: isOverlayThreads ? 5 : 1,
          transform: isOverlayThreads && !isThreadSidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: isOverlayThreads ? 'transform 0.2s ease' : 'none',
          boxShadow: isOverlayThreads && isThreadSidebarOpen ? '12px 0 40px rgba(15, 23, 42, 0.16)' : 'none'
        }}>
        <div style={{
          minHeight: '52px',
          padding: isMobileLayout ? '10px 12px' : '10px 12px 10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: `1px solid ${conductorTheme.border}`
        }}>
          <ClockRewind width={16} height={16} aria-hidden="true" style={{ color: conductorTheme.accentText, flexShrink: 0 }} />
          <h4 style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 700, margin: 0, color: conductorTheme.textStrong }}>Recent chats</h4>
          {renderNewMenu(
            <Button variant="secondary" size="sm" icon={Plus}>
              New
            </Button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>
          {isLoadingThreads ? (
            <div style={{ padding: '20px', textAlign: 'center', color: conductorTheme.textSubtle, fontSize: '12px' }}>
              Loading chats…
            </div>
          ) : threads.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: conductorTheme.textSubtle, fontSize: '12px' }}>
              No previous chats yet
            </div>
          ) : (
            threads.map((thread) => {
              const isActive = activeThreadId === thread.id;
              return (
                <div
                  key={thread.id}
                  className="venue-conductor-compact-thread-option"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? conductorTheme.accentSurfaceStrong : 'transparent'
                  }}
                >
                  <button
                    type="button"
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => selectThread(thread.id)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: '48px',
                      padding: '8px 6px 8px 10px',
                      border: 'none',
                      background: 'transparent',
                      color: conductorTheme.textStrong,
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      overflow: 'hidden',
                      fontSize: '12px',
                      fontWeight: isActive ? 700 : 600,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {thread.title || 'New chat'}
                    </div>
                    <div style={{ marginTop: '2px', color: conductorTheme.textSubtle, fontSize: '10px' }}>
                      {thread.message_count || 0} messages
                      {thread.last_message_at && ` · ${formatTimestamp(thread.last_message_at)}`}
                    </div>
                  </button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={Trash01}
                    aria-label="Delete conversation"
                    title="Delete conversation"
                    onClick={() => deleteThread(thread.id)}
                  />
                </div>
              );
            })
          )}
        </div>
        </div>}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {isMobileLayout && !condensedLayout && (
          <div style={{
            padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 12px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: conductorTheme.surface,
            flexShrink: 0
          }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="tertiary"
                size="sm"
                icon={Menu01}
                aria-label="Show conversations"
                onClick={() => setIsThreadSidebarOpen(true)}
              />
            </div>
            {/* 16px matches the visible content of the old 28px PNG box */}
            <OpendateLogo height={16} />
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {renderCuesToggle({ iconOnly: true })}
              {renderManualToggle({ iconOnly: true })}
              {renderNewMenu(
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={Plus}
                  aria-label="New chat or cue"
                />
              )}
              {toggleOrb && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={orbHidden ? EyeOff : Eye}
                  onClick={toggleOrb}
                  title={orbHidden ? 'Show floating shortcut' : 'Hide floating shortcut'}
                  aria-label={orbHidden ? 'Show floating Conductor shortcut' : 'Hide floating Conductor shortcut'}
                />
              )}
              {onCloseConductor && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={XClose}
                  aria-label="Close Conductor"
                  onClick={onCloseConductor}
                />
              )}
            </div>
          </div>
        )}

        {condensedLayout && (
          <header className={`venue-conductor-ui-header${isMobileLayout ? ' is-mobile' : ''}`}>
            <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 1 }}>
              <button
                type="button"
                className="venue-conductor-ui-chat-menu"
                aria-label={isThreadSidebarOpen ? 'Close recent chats' : 'Show recent chats'}
                aria-haspopup="dialog"
                aria-expanded={isThreadSidebarOpen}
                aria-controls={compactLayout ? 'venue-conductor-compact-thread-history' : undefined}
                onClick={() => setIsThreadSidebarOpen((isOpen) => !isOpen)}
              >
                <Conductor width={20} height={20} />
                <span>{activeThread?.title || 'New Chat'}</span>
                <ChevronDown width={16} height={16} aria-hidden="true" />
              </button>
              {renderNewMenu(
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Plus}
                  aria-label="New chat or cue"
                  title="New chat or cue"
                  className="venue-conductor-ui-icon-button"
                />
              )}
            </div>
            <div className="venue-conductor-ui-header-group venue-conductor-motion-stage" style={{ '--motion-stage': 2 }}>
              {renderCuesToggle({ iconOnly: isMobileLayout || compactLayout, inDockHeader: true })}
              {helpCenterEnabled && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={LifeBuoy01}
                  className={isMobileLayout || compactLayout ? 'venue-conductor-ui-icon-button is-ghost' : 'venue-conductor-ui-text-button'}
                  aria-label="Help Center"
                  aria-pressed={isManualPanelOpen}
                  title="Browse the Help Center without leaving the chat"
                  onClick={toggleManualPanel}
                >
                  {isMobileLayout || compactLayout ? null : 'Help'}
                </Button>
              )}
              {toggleOrb && orbHidden && !compactLayout && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={EyeOff}
                  onClick={toggleOrb}
                  title="Show floating shortcut"
                  aria-label="Show floating Conductor shortcut"
                  className="venue-conductor-ui-icon-button is-ghost"
                />
              )}
              {onExpandConductor && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={Maximize01}
                  onClick={onExpandConductor}
                  aria-label="Maximize Conductor"
                  title="Maximize"
                  className="venue-conductor-ui-icon-button is-ghost"
                />
              )}
              {onMinimizeConductor && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={Minimize01}
                  onClick={onMinimizeConductor}
                  aria-label="Minimize Conductor"
                  title="Minimize"
                  className="venue-conductor-ui-icon-button is-ghost"
                />
              )}
              {onCloseConductor && (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={Minus}
                  onClick={onCloseConductor}
                  aria-label="Collapse Conductor"
                  title="Collapse"
                  className="venue-conductor-ui-icon-button is-ghost"
                />
              )}
            </div>
          </header>
        )}

        {!condensedLayout && (
          <div style={{
            minHeight: isMobileLayout ? 'auto' : '60px',
            padding: isMobileLayout ? '0 12px 8px' : '12px 16px',
            display: 'flex',
            alignItems: 'center',
            borderBottom: isMobileLayout ? 'none' : `1px solid ${conductorTheme.border}`,
            flexShrink: 0
          }}>
            <ConductorThreadActivity
              activeToolCalls={activeToolCalls}
              completedToolCalls={threadToolCalls}
              resourceCards={threadResourceCards}
              referencedCards={threadReferencedCards}
              onOpenManualArticle={manualLinkHandler}
              usage={threadUsage}
              contextUsage={contextUsage}
              condensed={isMobileLayout}
            />
            {!isMobileLayout && (
              <>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  {renderCuesToggle()}
                  {renderManualToggle()}
                </div>
              </>
            )}
          </div>
        )}

        <div style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div ref={messagesScrollRef} onScroll={handleMessagesScroll} style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            filter: shouldDimMessagesForVoice ? 'blur(1.5px)' : 'none',
            opacity: shouldDimMessagesForVoice ? 0.42 : 1,
            transition: 'filter 0.2s ease, opacity 0.2s ease'
          }}>
            {shouldShowEmptyState ? (
              renderEmptyState()
            ) : isLoadingMessages ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: conductorTheme.textSubtle }}>
                Loading messages...
              </div>
            ) : (
              <>
                {threadHandoff && <ThreadContinuationNotice handoff={threadHandoff} />}
                {summaryBoundary && renderSummarizedMarker()}
                {messagesBeforeToolCalls.map((msg, i) => renderMessage(msg, i))}
                {renderToolCalls()}
                {anchoredToolCallMessage && renderMessage(anchoredToolCallMessage, anchoredToolCallMessageIndex)}
                {messagesAfterToolCalls.map((msg, i) => renderMessage(msg, anchoredToolCallMessageIndex + i + 1))}
                {renderStreamingMessage()}
                {renderUploadProgress()}
                {pendingApprovals.length > 0 && renderConfirmActionBar()}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {isVoiceStageVisible && renderVoiceStage()}
        </div>

        {showMcpPanel && (
          <div
            className={widgetLayout ? `venue-conductor-ai-panel${messages.length > 0 || isSending ? ' is-hidden' : ''}` : undefined}
            style={widgetLayout ? undefined : {
            padding: '12px 20px 0',
            backgroundColor: conductorTheme.surface,
            borderTop: `1px solid ${conductorTheme.border}`
          }}>
            <div>
              <ExistingAiPanel
                existingAiIntegration={existingAiIntegration}
                onCopy={copyToClipboard}
                onDismiss={dismissMcpPanel}
                onSetup={widgetLayout ? () => setIsActionMenuOpen(true) : undefined}
                isMobileLayout={isMobileLayout}
                variant={widgetLayout ? 'card' : 'default'}
              />
            </div>
          </div>
        )}

        <div style={{
          padding: condensedLayout ? `0 8px ${isMobileLayout ? 'calc(env(safe-area-inset-bottom, 0px) + 8px)' : '8px'}` : isMobileLayout ? '8px 12px calc(env(safe-area-inset-bottom, 0px) + 8px)' : '16px 20px',
          borderTop: showMcpPanel || (condensedLayout) ? 'none' : `1px solid ${conductorTheme.border}`,
          backgroundColor: conductorTheme.surface,
          flexShrink: 0
        }}>
          {!compactLayout && (
            <ContextHeadroomBanner
              contextUsage={contextUsage}
              onNewChatWithSummary={startNewChatWithSummary}
              onCompact={compactThread}
              isCreatingHandoff={isCreatingHandoff}
              isCompacting={isCompacting}
              actionsDisabled={headroomActionsDisabled}
              disabledReason={headroomActionsDisabledReason}
            />
          )}

          {!compactLayout && isVoiceNudgeVisible && voiceStatus === 'idle' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: `1px solid ${conductorTheme.borderBlue}`,
              borderRadius: '8px',
              backgroundColor: conductorTheme.surfaceSoft,
              color: conductorTheme.textSlate,
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '10px'
            }}>
              <Microphone01 width={12} height={12} aria-hidden="true" style={{ color: '#0f766e', flexShrink: 0 }} />
              <span style={{ flex: 1 }}>Tap the mic and say what you need.</span>
              <button
                type="button"
                aria-label="Dismiss voice tip"
                onClick={dismissVoiceNudge}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  border: `1px solid ${conductorTheme.borderMuted}`,
                  backgroundColor: conductorTheme.surface,
                  color: conductorTheme.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <XClose width={12} height={12} aria-hidden="true" />
              </button>
            </div>
          )}

          {!condensedLayout && <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobileLayout ? '6px' : '8px',
            marginBottom: isMobileLayout ? '8px' : '10px',
            alignItems: 'center'
          }}>
            <div ref={actionMenuRef} style={{ position: 'relative' }}>
              <Button
                variant={isActionMenuOpen || composerDirectives.length > 0 ? 'secondary-color' : 'secondary'}
                size="sm"
                icon={Plus}
                aria-label="Add to request"
                aria-haspopup="true"
                aria-expanded={isActionMenuOpen}
                title="Add a date range, export format, or action"
                className="venue-conductor-btn"
                onClick={() => setIsActionMenuOpen((isOpen) => !isOpen)}
              >
                Add
              </Button>
              {isActionMenuOpen && renderActionMenu()}
            </div>
          </div>}

          {!compactLayout && isDateRangePanelOpen && (
            <div style={{
              border: `1px solid ${conductorTheme.borderBlue}`,
              borderRadius: '14px',
              background: conductorTheme.surfaceSoft,
              padding: '12px',
              marginBottom: '10px',
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : '1.2fr 1fr 1fr auto',
                gap: '10px',
                alignItems: 'end'
              }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', fontWeight: 700, color: conductorTheme.textSoft }}>
                  Apply to
                  <select
                    value={dateRangeScope}
                    onChange={(e) => setDateRangeScope(e.target.value)}
                    style={{
                      border: `1px solid ${conductorTheme.borderMuted}`,
                      borderRadius: '10px',
                      padding: '8px 10px',
                      fontSize: '13px',
                      color: conductorTheme.text,
                      backgroundColor: conductorTheme.surface
                    }}
                  >
                    {DATE_RANGE_SCOPES.map((scope) => (
                      <option key={scope.value} value={scope.value}>{scope.label}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', fontWeight: 700, color: conductorTheme.textSoft }}>
                  Start
                  <input
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => setDateRangeStart(e.target.value)}
                    style={{
                      border: `1px solid ${conductorTheme.borderMuted}`,
                      borderRadius: '10px',
                      padding: '8px 10px',
                      fontSize: '13px',
                      color: conductorTheme.text,
                      backgroundColor: conductorTheme.surface
                    }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', fontWeight: 700, color: conductorTheme.textSoft }}>
                  End
                  <input
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => setDateRangeEnd(e.target.value)}
                    style={{
                      border: `1px solid ${conductorTheme.borderMuted}`,
                      borderRadius: '10px',
                      padding: '8px 10px',
                      fontSize: '13px',
                      color: conductorTheme.text,
                      backgroundColor: conductorTheme.surface
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={applyDateRange}
                  style={{
                    border: 'none',
                    borderRadius: '10px',
                    backgroundColor: '#1982C4',
                    color: '#fff',
                    padding: '9px 13px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Apply range
                </button>
              </div>
            </div>
          )}

          {composerDirectives.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '7px',
              marginBottom: '10px',
              alignItems: 'center'
            }}>
              {composerDirectives.map((directive) => (
                <span
                  key={directive.id}
                  title={directive.prompt}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                    border: `1px solid ${conductorTheme.accentBorder}`,
                    borderRadius: '999px',
                    backgroundColor: conductorTheme.accentSurfaceSoft,
                    color: conductorTheme.accentText,
                    padding: '5px 7px 5px 10px',
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: 1.2
                  }}
                >
                  <DirectiveIcon directive={directive} size={12} />
                  <span>{directive.kind}: {directive.label}</span>
                  <button
                    type="button"
                    aria-label={`Clear ${directive.kind}`}
                    onClick={() => removeComposerDirective(directive.id)}
                    style={{
                      width: '18px',
                      height: '18px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      borderRadius: '50%',
                      backgroundColor: conductorTheme.accentSurface,
                      color: conductorTheme.accentText,
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '11px',
                      lineHeight: 1
                    }}
                  >
                    <XClose width={11} height={11} aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {!condensedLayout && !shouldShowEmptyState && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
              <Button
                variant="link-gray"
                size="sm"
                icon={Lightbulb02}
                onClick={() => setShowPromptsPanel(true)}
                className="venue-conductor-btn"
              >
                Prompts to try
              </Button>
            </div>
          )}

          <div
            className={condensedLayout ? 'venue-conductor-ui-composer venue-conductor-motion-stage' : undefined}
            style={condensedLayout ? { '--motion-stage': 8 } : {
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end'
            }}
          >
            <textarea
              ref={inputRef}
              autoFocus={compactLayout}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={condensedLayout ? 'Ask Conductor…' : 'Ask me anything'}
              aria-label="Ask Conductor"
              rows={1}
              style={{
                flex: condensedLayout ? '1 0 100%' : 1,
                // Without minWidth:0 a flex child won't shrink below its min-content
                // (placeholder) width, so on narrow mobile the textarea + attach + mic
                // + send buttons overflow and the row wraps to two lines (O-3146).
                minWidth: 0,
                resize: 'none',
                border: condensedLayout ? 'none' : `1px solid ${conductorTheme.borderStrong}`,
                borderRadius: isMobileLayout ? '22px' : '8px',
                padding: isMobileLayout ? '11px 16px' : condensedLayout ? '8px 6px 4px 8px' : '10px 14px',
                fontSize: isMobileLayout || condensedLayout ? '16px' : '14px',
                lineHeight: condensedLayout ? '24px' : '1.4',
                outline: 'none',
                fontFamily: 'inherit',
                maxHeight: '120px',
                overflow: 'auto',
                backgroundColor: conductorTheme.surface,
                color: conductorTheme.text
              }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = conductorTheme.borderStrong}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.csv,.xlsx,.xls,.xlsm"
              style={{ display: 'none' }}
            />
            {widgetLayout ? (
              <div ref={actionMenuRef} className="venue-conductor-ui-attach">
                <button
                  type="button"
                  aria-label="Add to request"
                  aria-haspopup="true"
                  aria-expanded={isActionMenuOpen}
                  title="Attach a file, add a date range, export format, or action"
                  disabled={isUploadingFiles || isSending}
                  onClick={() => setIsActionMenuOpen((isOpen) => !isOpen)}
                  className="venue-conductor-btn"
                  style={{
                    ...composerActionButtonStyle,
                    width: '40px',
                    height: '40px',
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: isActionMenuOpen || composerDirectives.length > 0 ? conductorTheme.accentSurfaceMuted : 'transparent',
                    color: 'var(--od-c-475569, #535862)'
                  }}
                >
                  {isUploadingFiles ? (
                    <Spinner size={18} color="currentColor" />
                  ) : (
                    <Plus width={20} height={20} aria-hidden="true" />
                  )}
                </button>
                {isPromptMenuOpen && (
                  <ConductorPromptMenu
                    sections={promptMenuSections}
                    query={composerToken?.[2] || ''}
                    hint={composerToken?.[1] === '/' ? 'Type to search actions' : 'Type @ for sources or / for actions'}
                    onClose={() => setIsActionMenuOpen(false)}
                  />
                )}
              </div>
            ) : (
              <Button
                variant="secondary"
                icon={isUploadingFiles ? ComposerSpinner : Paperclip}
                onClick={handleAttachClick}
                disabled={isUploadingFiles || isSending}
                title="Attach a file (PDF, image or spreadsheet)"
                aria-label="Attach a file"
                className="venue-conductor-btn"
                style={composerActionButtonStyle}
              />
            )}
            {conductorVoiceEnabled && (
              <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <VoiceComposerButton
                  ghost={widgetLayout}
                  status={voiceStatus}
                  error={voiceError}
                  onStart={startVoiceMode}
                  onStop={endVoiceMode}
                  onDismissError={() => setVoiceError(null)}
                />
                {isMicTipVisible && voiceStatus === 'idle' && (
                  <div
                    role="status"
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 12px)',
                      right: 0,
                      width: '250px',
                      background: conductorTheme.surface,
                      color: conductorTheme.textStrong,
                      border: `1px solid ${conductorTheme.brandCoralBorder}`,
                      borderRadius: '12px',
                      padding: '12px 14px',
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
                      animation: 'conductor-tip-in 0.3s ease',
                      zIndex: 30,
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <Microphone01 width={16} height={16} aria-hidden="true" style={{ marginTop: '1px', flexShrink: 0, color: conductorTheme.brandCoral }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>
                          New: talk to Conductor
                        </div>
                        <div style={{ fontSize: '12px', lineHeight: 1.45, color: conductorTheme.textFaint }}>
                          Tap the mic and just say what you need — voice mode is here.
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label="Dismiss voice tip"
                        onClick={dismissMicTip}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: conductorTheme.textMuted,
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '13px',
                          lineHeight: 1,
                          flexShrink: 0
                        }}
                      >
                        <XClose width={13} height={13} aria-hidden="true" />
                      </button>
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: '18px',
                      width: 0,
                      height: 0,
                      borderLeft: '7px solid transparent',
                      borderRight: '7px solid transparent',
                      borderTop: '8px solid #ffffff'
                    }} />
                  </div>
                )}
              </div>
            )}
            {widgetLayout ? (
            <button
              onClick={() => sendMessage(null, { submitControl: 'send_button' })}
              disabled={!inputValue.trim() || isSending}
              aria-label="Send message"
              title="Send message"
              className="venue-conductor-btn"
              style={{
                ...composerActionButtonStyle,
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                border: 'none',
                cursor: !inputValue.trim() || isSending ? 'default' : 'pointer',
                backgroundColor: !inputValue.trim() || isSending ? 'var(--od-c-f3f4f6, #f5f5f5)' : '#1982c4',
                color: !inputValue.trim() || isSending ? '#a4a7ae' : '#fff',
                boxShadow: !inputValue.trim() || isSending
                  ? 'none'
                  : '0 1px 2px rgba(10, 13, 18, 0.05), inset 0 0 0 1px rgba(10, 13, 18, 0.18), inset 0 -2px 0 rgba(10, 13, 18, 0.05)'
              }}
            >
              {isSending ? (
                <Spinner size={18} color="currentColor" />
              ) : (
                <ArrowUp width={20} height={20} aria-hidden="true" />
              )}
            </button>
            ) : (
            <Button
              variant="primary"
              icon={isSending ? ComposerSpinner : Send01}
              onClick={() => sendMessage(null, { submitControl: 'send_button' })}
              disabled={!inputValue.trim() || isSending}
              aria-label="Send message"
              title="Send message"
              className="venue-conductor-btn"
              style={composerActionButtonStyle}
            />
            )}
          </div>
        </div>
      </div>

      {helpCenterEnabled && isManualPanelOpen && (
        <ConductorManualPanel
          sections={userManual.sections}
          loading={userManual.loading}
          error={userManual.error}
          onRetry={userManual.reload}
          onClose={() => setIsManualPanelOpen(false)}
          onAskConductor={(prompt) => {
            setIsManualPanelOpen(false);
            handleSuggestionClick(prompt);
          }}
          requestedArticle={manualArticleRequest}
        />
      )}

      {conductorCuesEnabled && cuesPanelView && (
        <ConductorCuesPanel
          initialView={cuesPanelView}
          onClose={() => setCuesPanelView(null)}
          onNewCue={startNewCue}
          onOpenThread={openCueThread}
          onAskConductor={(prompt) => {
            setCuesPanelView(null);
            handleSuggestionClick(prompt);
          }}
        />
      )}

      {showPromptsPanel && (
        <PromptsToTryPanel
          catalog={buildPromptCatalog(effectivePromptContext || {})}
          isMobileLayout={isMobileLayout}
          onClose={() => setShowPromptsPanel(false)}
          onSelect={(prompt) => {
            setShowPromptsPanel(false);
            handleSuggestionClick(prompt);
          }}
        />
      )}
    </div>
    </ManualLinkContext.Provider>
  );
};

VenueConductorPage.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  containerHeight: PropTypes.string,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  teamName: PropTypes.string,
  currentUserAvatarUrl: PropTypes.string,
  existingAiIntegration: existingAiIntegrationPropType,
  conductorVoiceEnabled: PropTypes.bool,
  helpCenterEnabled: PropTypes.bool,
  conductorTextingEnabled: PropTypes.bool,
  conductorCuesEnabled: PropTypes.bool,
  renderToastContainer: PropTypes.bool,
  initialPrompt: PropTypes.string,
  autoStartVoice: PropTypes.bool,
  onCloseConductor: PropTypes.func,
  onExpandConductor: PropTypes.func,
  onMinimizeConductor: PropTypes.func,
  onVoiceStateChange: PropTypes.func,
  voiceEndRequestId: PropTypes.number,
  orbHidden: PropTypes.bool,
  toggleOrb: PropTypes.func,
  promptContext: PropTypes.shape({
    venueName: PropTypes.string,
    secondVenueName: PropTypes.string,
    upcomingEventName: PropTypes.string,
    pastEventName: PropTypes.string
  }),
  initialThreadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onActiveThreadChange: PropTypes.func,
  conductorInterface: PropTypes.oneOf(['widget', 'dock', 'mobile', 'voice_call', 'page']),
  entryPoint: PropTypes.string,
  layoutMode: PropTypes.oneOf(['full', 'condensed', 'compact'])
};

export default VenueConductorPage;
